import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  FileText,
  Video,
  FileType,
  Presentation,
  Link as LinkIcon,
  Folder,
  FolderOpen,
  Search,
  Grid,
  List,
  Download,
  Eye,
  Upload,
  Plus,
  MoreVertical,
  Trash2,
  CheckSquare,
  Square,
  Package,
  BookOpen,
  GraduationCap,
  Image as ImageIcon,
  File,
  ChevronRight,
  Home,
  X,
  GripVertical,
  ExternalLink,
  Copy,
  Pencil,
  ArrowUp,
  ArrowDown,
  Cloud,
  RefreshCw,
  Briefcase,
  Heart,
  Star,
  Lightbulb,
  Target,
  Award,
  Calendar,
  Shield,
  Users,
  Wrench,
  ClipboardList,
  Palette,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CurriculumPage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolder = searchParams.get('folder');
  const currentSection = searchParams.get('section');
  const SECTION_LABELS = {
    curriculum: 'Curriculum',
    work_based_learning: 'Work-Based Learning',
    toolkit: 'Toolkit Resources',
    training_files: 'Training Files',
  };
  // Navigate folders via the URL so the browser Back button steps back one folder level.
  // The active section (if any) rides along so left-rail highlighting + context persist.
  const navigateToFolder = (folderId) => {
    const sp = {};
    if (currentSection) sp.section = currentSection;
    if (folderId) sp.folder = folderId;
    setSearchParams(sp);
  };
  const navigateToRoot = () => setSearchParams({});
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [isLoading, setIsLoading] = useState(true);
  
  // Selection state
  const [selectedItems, setSelectedItems] = useState({ documents: [], folders: [] });
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadMode, setUploadMode] = useState('single'); // 'single' or 'bulk'
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    content_type: 'other',
    tags: ''
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Create folder dialog
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderData, setNewFolderData] = useState({
    name: '',
    description: '',
    folder_type: 'other'
  });
  
  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  
  // Rename folder state
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderTarget, setRenameFolderTarget] = useState(null);
  const [renameFolderValue, setRenameFolderValue] = useState('');

  // Folder download confirmation (shows file count + subfolder hint)
  const [downloadConfirm, setDownloadConfirm] = useState({ open: false, folder: null, format: 'pdf', count: null, subfolders: [], loading: false });

  const openDownloadConfirm = async (folder, format) => {
    setDownloadConfirm({ open: true, folder, format, count: null, subfolders: [], loading: true });
    try {
      const res = await axios.get(`${API}/folders/${folder.id}/file-count`);
      setDownloadConfirm((prev) => ({ ...prev, count: res.data.count, subfolders: res.data.subfolders || [], loading: false }));
    } catch (e) {
      setDownloadConfirm((prev) => ({ ...prev, count: null, subfolders: [], loading: false }));
    }
  };

  const confirmFolderDownload = () => {
    const { folder, format } = downloadConfirm;
    setDownloadConfirm({ open: false, folder: null, format: 'pdf', count: null, subfolders: [], loading: false });
    if (folder) downloadFolder(folder.id, folder.name, format);
  };

  const downloadSubfolder = (sub) => {
    const { format } = downloadConfirm;
    setDownloadConfirm({ open: false, folder: null, format: 'pdf', count: null, subfolders: [], loading: false });
    downloadFolder(sub.id, sub.name, format);
  };

  // Folder appearance (color + icon) customization
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [appearanceTarget, setAppearanceTarget] = useState(null);
  const [appearanceColor, setAppearanceColor] = useState(null);
  const [appearanceIcon, setAppearanceIcon] = useState(null);

  // Recently added documents (curriculum landing strip)
  const [recentDocs, setRecentDocs] = useState([]);

  // Google Drive import dialog
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveStatus, setDriveStatus] = useState({ configured: false, service_account_email: null });
  const [driveUrl, setDriveUrl] = useState('');
  const [driveContentType, setDriveContentType] = useState('other');
  const [drivePreview, setDrivePreview] = useState(null); // {id, name, is_folder, tree?, file_count?, folder_count?, mimeType, webViewLink}
  const [driveLoadingPreview, setDriveLoadingPreview] = useState(false);
  const [driveImporting, setDriveImporting] = useState(false);
  const [driveRecursive, setDriveRecursive] = useState(true);
  const [driveSection, setDriveSection] = useState('curriculum');

  // Drive sync state
  const [syncingFolder, setSyncingFolder] = useState(false);
  const [syncingDocs, setSyncingDocs] = useState(new Set());
  const [clearSectionOpen, setClearSectionOpen] = useState(false);
  const [clearingSection, setClearingSection] = useState(false);
  const [clearConfirmText, setClearConfirmText] = useState('');
  
  // PDF Preview dialog
  const [previewDoc, setPreviewDoc] = useState(null);
  const [formCopyPrompt, setFormCopyPrompt] = useState(null);
  const [copyReceipt, setCopyReceipt] = useState(null);
  const pendingReceiptRef = useRef(null);

  const closeFormPrompt = (movedForms = []) => {
    setFormCopyPrompt(null);
    if (pendingReceiptRef.current) {
      const r = pendingReceiptRef.current;
      pendingReceiptRef.current = null;
      setCopyReceipt({ ...r, files: [...r.files, ...movedForms] });
    } else if (movedForms.length) {
      setCopyReceipt({ title: movedForms.length === 1 ? movedForms[0].title : 'Form copies', folderLink: null, files: movedForms, skipped: [], formsPending: [] });
    }
  };
  const [copyDestPrompt, setCopyDestPrompt] = useState(null);
  const [lastDest, setLastDest] = useState(null);
  
  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedType, setDraggedType] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  const dragRef = useRef({ item: null, type: null });
  const mimeBackfilledRef = useRef(new Set());

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (currentFolder) {
        // Normal folder view
        const [docsRes, foldersRes, breadcrumbRes] = await Promise.all([
          axios.get(`${API}/documents`, { params: { folder_id: currentFolder } }),
          axios.get(`${API}/folders`, { params: { parent_id: currentFolder } }),
          axios.get(`${API}/folders/${currentFolder}/breadcrumb`),
        ]);
        setDocuments(docsRes.data);
        setFolders(foldersRes.data);
        setBreadcrumb(breadcrumbRes.data);
      } else if (currentSection) {
        // Section landing: show the folders assigned to this section
        const secRes = await axios.get(`${API}/folders`, { params: { section: currentSection } });
        setFolders(secRes.data);
        setDocuments([]);
        setBreadcrumb([]);
      } else {
        // Curriculum root
        const [docsRes, foldersRes] = await Promise.all([
          axios.get(`${API}/documents`, { params: { folder_id: undefined } }),
          axios.get(`${API}/folders`, { params: { parent_id: 'null' } }),
        ]);
        setDocuments(docsRes.data);
        setFolders(foldersRes.data);
        setBreadcrumb([]);
        try {
          const recentRes = await axios.get(`${API}/documents/recent`, { params: { limit: 8 } });
          setRecentDocs(recentRes.data);
        } catch (e) { /* non-critical */ }
      }
    } catch (error) {
      console.error('Error fetching curriculum data:', error);
      toast.error('Failed to load curriculum');
    } finally {
      setIsLoading(false);
    }
  }, [currentFolder, currentSection]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // If a section maps to exactly one folder, drill straight into it (keeps section context in URL)
  useEffect(() => {
    if (currentSection && !currentFolder && !isLoading && folders.length === 1) {
      setSearchParams({ section: currentSection, folder: folders[0].id });
    }
  }, [currentSection, currentFolder, isLoading, folders, setSearchParams]);

  // Refetch when an admin drags a folder to a different section in the left rail
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('folder-section-changed', handler);
    return () => window.removeEventListener('folder-section-changed', handler);
  }, [fetchData]);

  // Helpers: relative + absolute time formatting for Drive sync badge
  const formatRelativeTime = (iso) => {
    if (!iso) return 'never';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return 'unknown';
    const diff = Date.now() - then;
    if (diff < 0) return 'just now';
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d} day${d > 1 ? 's' : ''} ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo} month${mo > 1 ? 's' : ''} ago`;
    const y = Math.floor(mo / 12);
    return `${y} year${y > 1 ? 's' : ''} ago`;
  };
  const formatAbsoluteDate = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString();
    } catch (e) {
      return iso;
    }
  };

  // Returns {label, dotColor, tooltip} describing the doc's Drive sync state.
  const getDriveSyncState = (doc) => {
    if (!doc.drive_file_id) return null;
    if (doc.drive_status === 'missing') {
      return { label: 'Removed from Drive', dotColor: 'bg-red-500', tooltip: 'This file is no longer accessible on Drive' };
    }
    if (doc.drive_status === 'trashed') {
      return { label: 'Trashed in Drive', dotColor: 'bg-red-500', tooltip: 'This file was moved to Drive trash' };
    }
    const cur = doc.drive_modified_time;
    const baseline = doc.drive_modified_time_at_import;
    if (!cur) {
      return {
        label: 'Awaiting sync',
        dotColor: 'bg-slate-300',
        tooltip: 'Drive sync has not run yet for this document',
        absolute: '',
      };
    }
    const isNewer = baseline && cur > baseline;
    return {
      label: `Drive updated ${formatRelativeTime(cur)}`,
      dotColor: isNewer ? 'bg-amber-500' : 'bg-emerald-500',
      tooltip: `Drive modifiedTime: ${formatAbsoluteDate(cur)}${isNewer ? ' — newer than when imported' : ''}`,
      absolute: formatAbsoluteDate(cur),
      isNewer,
    };
  };

  // Auto-sync Drive metadata once per hour per folder, in the background.
  useEffect(() => {
    const admin = user?.role === 'system_admin' || user?.role === 'team_admin';
    if (!admin) return;  // sync-folder is admin-only
    const driveDocs = documents.filter(d => d.drive_file_id);
    if (driveDocs.length === 0) return;
    const key = `drive-sync-${currentFolder || 'root'}`;
    const last = parseInt(localStorage.getItem(key) || '0', 10);
    const ONE_HOUR = 60 * 60 * 1000;
    if (Date.now() - last < ONE_HOUR) return;
    // Fire-and-forget — don't block UI
    const target = currentFolder || 'root';
    axios.post(`${API}/drive/sync-folder/${target}?recursive=false`, null, { timeout: 60000 })
      .then(res => {
        localStorage.setItem(key, String(Date.now()));
        const { updated, missing } = res.data || {};
        if (updated > 0 || missing > 0) {
          // Silently refresh card data so badges turn amber
          fetchData();
          if (updated > 0) {
            toast.info(`${updated} Drive ${updated === 1 ? 'file has' : 'files have'} been updated since import`);
          }
          if (missing > 0) {
            toast.warning(`${missing} Drive ${missing === 1 ? 'file is' : 'files are'} no longer accessible`);
          }
        }
      })
      .catch(() => { /* ignore background errors */ });
  // We intentionally depend only on the doc list length + folder, not on fetchData itself.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents.length, currentFolder]);

  // One-time backfill of Drive mime types (needed for the smart download menu on
  // docs imported before we started storing mimeType). Runs once per folder per mount.
  useEffect(() => {
    const admin = user?.role === 'system_admin' || user?.role === 'team_admin';
    if (!admin) return;  // sync-folder is admin-only
    const key = currentFolder || 'root';
    if (mimeBackfilledRef.current.has(key)) return;
    const needsBackfill = documents.some(d => d.drive_file_id && !d.drive_mime_type);
    if (!needsBackfill) return;
    mimeBackfilledRef.current.add(key);
    axios.post(`${API}/drive/sync-folder/${key}?recursive=false`, null, { timeout: 120000 })
      .then(() => fetchData())
      .catch(() => { /* ignore background errors */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents, currentFolder]);

  const handleManualSyncFolder = async () => {
    setSyncingFolder(true);
    try {
      const target = currentFolder || 'root';
      const res = await axios.post(`${API}/drive/sync-folder/${target}?recursive=true`, null, { timeout: 180000 });
      const { synced, updated, missing, errors, folders_renamed, new_files, new_folders } = res.data || {};
      localStorage.setItem(`drive-sync-${currentFolder || 'root'}`, String(Date.now()));
      let msg = `Checked ${synced} Drive file${synced === 1 ? '' : 's'}`;
      if (new_files) msg += ` • ${new_files} new file${new_files === 1 ? '' : 's'} added`;
      if (new_folders) msg += ` • ${new_folders} new folder${new_folders === 1 ? '' : 's'} added`;
      if (updated) msg += ` • ${updated} updated`;
      if (folders_renamed) msg += ` • ${folders_renamed} folder${folders_renamed === 1 ? '' : 's'} renamed`;
      if (missing) msg += ` • ${missing} missing`;
      if (errors) msg += ` • ${errors} errored`;
      toast.success(msg);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncingFolder(false);
    }
  };

  const handleSyncDocument = async (docId) => {
    setSyncingDocs(prev => new Set(prev).add(docId));
    try {
      const res = await axios.post(`${API}/drive/sync-document/${docId}`, null, { timeout: 30000 });
      const status = res.data?.result?.status;
      if (status === 'updated') toast.success('Drive metadata refreshed — new version available');
      else if (status === 'missing') toast.warning('This file is no longer accessible on Drive');
      else toast.success('Drive metadata refreshed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Refresh failed');
    } finally {
      setSyncingDocs(prev => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  const folderHasDriveDocs = documents.some(d => d.drive_file_id);
  const isDemo = user?.role === 'demo';
  const isAdmin = user?.role === 'system_admin' || user?.role === 'team_admin';

  // Diagonal, tiled "Preview copy" watermark shown over the file viewer for demo accounts
  const demoWatermarkStyle = {
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
      "<svg xmlns='http://www.w3.org/2000/svg' width='470' height='300'><text x='235' y='150' transform='rotate(-30 235 150)' fill='rgba(52,101,127,0.16)' font-family='Arial, Helvetica, sans-serif' font-size='22' font-weight='700' text-anchor='middle'>Preview copy \u2014 not for distribution</text></svg>"
    )}")`,
    backgroundRepeat: 'repeat',
  };

  // Clear selection when changing folders
  useEffect(() => {
    setSelectedItems({ documents: [], folders: [] });
    setSelectionMode(false);
  }, [currentFolder]);

  const getDocIcon = (type) => {
    const icons = {
      pdf: { icon: FileText, color: 'bg-red-500' },
      video: { icon: Video, color: 'bg-purple-500' },
      document: { icon: FileType, color: 'bg-[#34657f]' },
      presentation: { icon: Presentation, color: 'bg-[#b34e00]' },
      link: { icon: LinkIcon, color: 'bg-[#3d7a22]' },
      image: { icon: ImageIcon, color: 'bg-pink-500' },
      other: { icon: File, color: 'bg-slate-500' },
    };
    return icons[type] || icons.other;
  };

  const getFolderIcon = (type) => {
    const icons = {
      unit: { icon: Package, color: 'bg-[#34657f]' },
      lesson: { icon: BookOpen, color: 'bg-[#b34e00]' },
      activity: { icon: GraduationCap, color: 'bg-[#3d7a22]' },
      module: { icon: Folder, color: 'bg-[#b34e00]' },
      other: { icon: FolderOpen, color: 'bg-slate-500' },
    };
    return icons[type] || icons.other;
  };

  // Canonical document order within a competency folder.
  const DOC_TYPE_ORDER = ['lesson plan', 'presentation', 'google form', 'worksheet', 'exit ticket'];
  const getDocRank = (title) => {
    const t = (title || '').toLowerCase();
    for (let i = 0; i < DOC_TYPE_ORDER.length; i++) {
      if (t.includes(DOC_TYPE_ORDER[i])) return i;
    }
    return DOC_TYPE_ORDER.length; // extras / misc
  };
  const DOC_TYPE_BADGES = {
    'lesson plan': { label: 'Lesson Plan', className: 'bg-[#34657f] text-white hover:bg-[#34657f]' },
    'presentation': { label: 'Presentation', className: 'bg-[#b34e00] text-white hover:bg-[#b34e00]' },
    'google form': { label: 'Google Form', className: 'bg-[#3d7a22] text-white hover:bg-[#3d7a22]' },
    'worksheet': { label: 'Worksheet', className: 'bg-[#1f7fa6] text-white hover:bg-[#1f7fa6]' },
    'exit ticket': { label: 'Exit Ticket', className: 'bg-[#a855f7] text-white hover:bg-[#a855f7]' },
  };
  const getDocTypeBadge = (title) => {
    const t = (title || '').toLowerCase();
    for (const key of Object.keys(DOC_TYPE_BADGES)) {
      if (t.includes(key)) return DOC_TYPE_BADGES[key];
    }
    return null;
  };

  const filteredDocuments = documents
    .filter((doc) => {
      const matchesSearch = doc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = filterType === 'all' || doc.doc_type === filterType;
      return matchesSearch && matchesType;
    })
    .sort((a, b) => {
      // Only enforce the canonical type order inside a specific (competency) folder.
      if (!currentFolder) return 0;
      const ra = getDocRank(a.title);
      const rb = getDocRank(b.title);
      if (ra !== rb) return ra - rb;
      // Extras sort alphabetically; known types keep a stable, title-based order.
      return (a.title || '').localeCompare(b.title || '', undefined, { numeric: true });
    });

  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
    (!folder.hidden || isAdmin)
  );

  // Deep section-wide search (all nested folders + files in the current left-rail section)
  const [deepResults, setDeepResults] = useState(null);
  useEffect(() => {
    if (!currentSection || (!searchQuery.trim() && filterType === 'all')) {
      setDeepResults(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/curriculum/search`, {
          params: {
            section: currentSection,
            q: searchQuery.trim() || undefined,
            doc_type: filterType !== 'all' ? filterType : undefined,
          },
        });
        setDeepResults(res.data);
      } catch {
        setDeepResults(null);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery, filterType, currentSection]);

  const displayFolders = deepResults
    ? deepResults.folders.filter((f) => !f.hidden || isAdmin)
    : filteredFolders;
  const displayDocuments = deepResults ? deepResults.documents : filteredDocuments;

  const handleFolderClick = (folderId) => {
    if (reorderMode) return;
    navigateToFolder(folderId);
    setSearchQuery('');
  };

  const handleBreadcrumbClick = (folderId) => {
    navigateToFolder(folderId);
  };

  // Selection handlers
  const toggleDocumentSelection = (docId) => {
    setSelectedItems(prev => ({
      ...prev,
      documents: prev.documents.includes(docId)
        ? prev.documents.filter(id => id !== docId)
        : [...prev.documents, docId]
    }));
  };

  const toggleFolderSelection = (folderId) => {
    setSelectedItems(prev => ({
      ...prev,
      folders: prev.folders.includes(folderId)
        ? prev.folders.filter(id => id !== folderId)
        : [...prev.folders, folderId]
    }));
  };

  const selectAll = () => {
    setSelectedItems({
      documents: displayDocuments.map(d => d.id),
      folders: displayFolders.map(f => f.id)
    });
  };

  const clearSelection = () => {
    setSelectedItems({ documents: [], folders: [] });
    setSelectionMode(false);
  };

  const totalSelected = selectedItems.documents.length + selectedItems.folders.length;

  // Download handlers
  const downloadMultiple = async (format = 'pdf') => {
    const docsToDownload = selectedItems.documents;
    if (docsToDownload.length === 0) {
      toast.error('No files selected');
      return;
    }
    const tId = toast.loading(`Preparing ${docsToDownload.length} file${docsToDownload.length !== 1 ? 's' : ''}…`);
    try {
      const response = await axios.post(
        `${API}/documents/download-multiple?format=${format}`,
        docsToDownload,
        {
          responseType: 'blob',
          onDownloadProgress: (e) => {
            const mb = (e.loaded / (1024 * 1024)).toFixed(1);
            toast.loading(`Preparing files… ${mb} MB received`, { id: tId });
          },
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'documents.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloaded ${docsToDownload.length} file${docsToDownload.length !== 1 ? 's' : ''}`, { id: tId });
      clearSelection();
    } catch (error) {
      toast.error('Failed to download files', { id: tId });
    }
  };

  const downloadFolder = async (folderId, folderName, format = 'pdf') => {
    const tId = toast.loading(`Preparing ${folderName}…`);
    try {
      const response = await axios.post(
        `${API}/documents/download-folder/${folderId}?format=${format}`,
        {},
        {
          responseType: 'blob',
          onDownloadProgress: (e) => {
            const mb = (e.loaded / (1024 * 1024)).toFixed(1);
            toast.loading(`Preparing ${folderName}… ${mb} MB received`, { id: tId });
          },
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${folderName.replace(/\s+/g, '_')}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloaded ${folderName}`, { id: tId });
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No files in this folder to download', { id: tId });
      } else {
        toast.error('Failed to download folder', { id: tId });
      }
    }
  };

  const [resyncingFolderId, setResyncingFolderId] = useState(null);

  const clearSectionContent = async () => {
    if (!currentSection) return;
    setClearingSection(true);
    try {
      const res = await axios.delete(`${API}/sections/${currentSection}/content`, { params: { user_role: user?.role || 'instructor' } });
      toast.success(`Cleared ${res.data.folders_deleted} folder(s) and ${res.data.documents_deleted} file(s) from ${SECTION_LABELS[currentSection]}`);
      setClearSectionOpen(false);
      setClearConfirmText('');
      setSearchParams({ section: currentSection });
      await fetchData();
      window.dispatchEvent(new CustomEvent('folder-section-changed'));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear section');
    } finally {
      setClearingSection(false);
    }
  };

  const resyncFolder = async (folder) => {
    if (!folder.drive_folder_id) {
      toast.error('This folder is not linked to Google Drive');
      return;
    }
    setResyncingFolderId(folder.id);
    const tId = toast.loading(`Re-syncing "${folder.name}" from Drive…`);
    try {
      const res = await axios.post(`${API}/drive/resync-folder/${folder.id}`);
      const { files_added = 0, folders_added = 0, files_updated = 0, files_removed = 0 } = res.data || {};
      toast.success(
        `Re-synced "${folder.name}" — ${files_added} new, ${folders_added} new folder${folders_added === 1 ? '' : 's'}, ${files_updated} updated${files_removed ? `, ${files_removed} flagged as removed` : ''}`,
        { id: tId }
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-sync folder', { id: tId });
    } finally {
      setResyncingFolderId(null);
    }
  };

  // Upload handler
  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('title', uploadData.title);
    formData.append('description', uploadData.description);
    formData.append('content_type', uploadData.content_type);
    formData.append('folder_id', currentFolder || '');
    formData.append('tags', uploadData.tags);
    formData.append('created_by', user?.id || 'anonymous');
    formData.append('created_by_name', user?.name || 'Anonymous');
    
    try {
      await axios.post(`${API}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      toast.success('File uploaded successfully!');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadData({ title: '', description: '', content_type: 'other', tags: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Bulk upload handler
  const handleBulkUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Please select files');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    uploadFiles.forEach(file => formData.append('files', file));
    formData.append('folder_id', currentFolder || '');
    formData.append('content_type', uploadData.content_type);
    formData.append('created_by', user?.id || 'anonymous');
    formData.append('created_by_name', user?.name || 'Anonymous');

    try {
      const response = await axios.post(`${API}/documents/upload-bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success(`${response.data.uploaded} files uploaded!`);
      setUploadOpen(false);
      setUploadFiles([]);
      setUploadMode('single');
      setUploadData({ title: '', description: '', content_type: 'other', tags: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Create folder handler
  const handleCreateFolder = async () => {
    if (!newFolderData.name.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    
    try {
      await axios.post(`${API}/folders`, {
        name: newFolderData.name,
        description: newFolderData.description,
        folder_type: newFolderData.folder_type,
        parent_id: currentFolder
      }, {
        params: { created_by: user?.id || 'anonymous' }
      });
      
      toast.success('Folder created successfully!');
      setCreateFolderOpen(false);
      setNewFolderData({ name: '', description: '', folder_type: 'other' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteTarget || user?.role !== 'system_admin') return;
    
    try {
      if (deleteType === 'document') {
        await axios.delete(`${API}/documents/${deleteTarget.id}`, {
          params: { user_role: user.role }
        });
        toast.success('Document deleted');
      } else {
        await axios.delete(`${API}/folders/${deleteTarget.id}`, {
          params: { user_role: user.role }
        });
        toast.success('Folder deleted');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteTarget(null);
      setDeleteType(null);
    }
  };

  // Rename folder handler
  const handleRenameFolder = async () => {
    if (!renameFolderTarget || !renameFolderValue.trim()) return;
    try {
      await axios.put(`${API}/folders/${renameFolderTarget.id}/rename`, {
        name: renameFolderValue.trim()
      });
      toast.success('Folder renamed');
      setRenameFolderOpen(false);
      setRenameFolderTarget(null);
      setRenameFolderValue('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rename folder');
    }
  };

  // Google Drive: fetch configuration status (called when opening dialog)
  const openDriveDialog = async () => {
    setDriveOpen(true);
    setDrivePreview(null);
    setDriveUrl('');
    setDriveSection(currentSection || 'curriculum');
    try {
      const res = await axios.get(`${API}/drive/status`);
      setDriveStatus(res.data || { configured: false });
    } catch (e) {
      setDriveStatus({ configured: false });
    }
  };

  const handleDrivePreview = async () => {
    if (!driveUrl.trim()) {
      toast.error('Please paste a Google Drive URL or ID');
      return;
    }
    setDriveLoadingPreview(true);
    setDrivePreview(null);
    try {
      const res = await axios.post(`${API}/drive/preview`, {
        url: driveUrl.trim(),
        recursive: driveRecursive,
      });
      setDrivePreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview Drive item');
    } finally {
      setDriveLoadingPreview(false);
    }
  };

  const handleDriveImport = async () => {
    if (!drivePreview) {
      toast.error('Please preview the item first');
      return;
    }
    setDriveImporting(true);
    try {
      if (drivePreview.is_folder) {
        // Folder imports become a top-level folder of the chosen left-menu section,
        // so each section (Curriculum / Work-Based Learning / Toolkit / Training Files)
        // can sync its own separate Drive folder.
        const res = await axios.post(`${API}/drive/import-folder`, {
          url: driveUrl.trim(),
          destination_folder_id: null,
          content_type: driveContentType,
          recursive: driveRecursive,
          section: driveSection,
        }, {
          params: {
            created_by: user?.id || 'anonymous',
            created_by_name: user?.name || 'Anonymous',
          }
        });
        const d = res.data || {};
        toast.success(`Imported ${d.folders_created || 0} folders and ${d.files_created || 0} files from Drive`);
      } else {
        await axios.post(`${API}/drive/import-file`, {
          url: driveUrl.trim(),
          content_type: driveContentType,
          folder_id: currentFolder,
        }, {
          params: {
            created_by: user?.id || 'anonymous',
            created_by_name: user?.name || 'Anonymous',
          }
        });
        toast.success('File imported from Google Drive');
      }
      setDriveOpen(false);
      setDriveUrl('');
      setDrivePreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import from Google Drive');
    } finally {
      setDriveImporting(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, item, type) => {
    dragRef.current = { item, type };
    setDraggedItem(item);
    setDraggedType(type);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', item.id);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetItem, targetType) => {
    e.preventDefault();
    if (!reorderMode) return; // reordering only happens in reorder mode
    
    const dragged = dragRef.current;
    if (!dragged.item || dragged.type !== targetType) return;
    
    const items = targetType === 'folder' ? [...folders] : [...documents];
    const draggedIndex = items.findIndex(i => i.id === dragged.item.id);
    const targetIndex = items.findIndex(i => i.id === targetItem.id);
    
    if (draggedIndex === targetIndex || draggedIndex === -1 || targetIndex === -1) return;
    
    const [removed] = items.splice(draggedIndex, 1);
    items.splice(targetIndex, 0, removed);
    
    const reorderData = items.map((item, index) => ({
      id: item.id,
      order: index
    }));
    
    if (targetType === 'folder') {
      setFolders(items);
      try {
        await axios.put(`${API}/folders/reorder-batch`, reorderData);
        toast.success('Folders reordered');
      } catch (error) {
        toast.error('Failed to save order');
        fetchData();
      }
    } else {
      setDocuments(items);
      try {
        await axios.put(`${API}/documents/reorder-batch`, reorderData);
        toast.success('Documents reordered');
      } catch (error) {
        toast.error('Failed to save order');
        fetchData();
      }
    }
    
    dragRef.current = { item: null, type: null };
    setDraggedItem(null);
    setDraggedType(null);
  };

  const handleDragEnd = () => {
    dragRef.current = { item: null, type: null };
    setDraggedItem(null);
    setDraggedType(null);
  };

  // Move up/down handlers for reliable reordering
  const handleMoveFolder = async (folderId, direction) => {
    const idx = folders.findIndex(f => f.id === folderId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= folders.length) return;

    const updated = [...folders];
    [updated[idx], updated[swapIdx]] = [updated[swapIdx], updated[idx]];
    setFolders(updated);

    const reorderData = updated.map((item, i) => ({ id: item.id, order: i }));
    try {
      await axios.put(`${API}/folders/reorder-batch`, reorderData);
    } catch (error) {
      toast.error('Failed to save order');
      fetchData();
    }
  };

  const handleMoveDocument = async (docId, direction) => {
    const idx = documents.findIndex(d => d.id === docId);
    if (idx === -1) return;
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= documents.length) return;

    const updated = [...documents];
    [updated[idx], updated[swapIdx]] = [updated[swapIdx], updated[idx]];
    setDocuments(updated);

    const reorderData = updated.map((item, i) => ({ id: item.id, order: i }));
    try {
      await axios.put(`${API}/documents/reorder-batch`, reorderData);
    } catch (error) {
      toast.error('Failed to save order');
      fetchData();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Check if file is previewable (uploaded PDFs preview via toolbar; Drive files preview inline)
  const canPreview = (doc) => {
    return doc.doc_type === 'pdf' && doc.file_url && !doc.drive_file_id;
  };

  // Build an embeddable preview URL for the in-app viewer
  const getPreviewUrl = (doc) => {
    if (doc.drive_file_id) {
      const id = doc.drive_file_id;
      const m = doc.drive_mime_type || '';
      if (m === 'application/vnd.google-apps.document') return `https://docs.google.com/document/d/${id}/preview`;
      if (m === 'application/vnd.google-apps.spreadsheet') return `https://docs.google.com/spreadsheets/d/${id}/preview`;
      if (m === 'application/vnd.google-apps.presentation') return `https://docs.google.com/presentation/d/${id}/preview`;
      if (m === 'application/vnd.google-apps.form') return (doc.file_url || '').replace('/edit', '/viewform');
      return `https://drive.google.com/file/d/${id}/preview`;
    }
    if (doc.file_url) return `${process.env.REACT_APP_BACKEND_URL}${doc.file_url}#toolbar=1`;
    return null;
  };

  const isPreviewable = (doc) => !!getPreviewUrl(doc);

  // For Google-native Drive docs, return the editable-format label; null for binary/uploaded files
  const getDriveNativeKind = (doc) => {
    if (!doc.drive_file_id) return null;
    const m = doc.drive_mime_type;
    if (m === 'application/vnd.google-apps.document') return { label: 'Word', ext: 'docx' };
    if (m === 'application/vnd.google-apps.spreadsheet') return { label: 'Excel', ext: 'xlsx' };
    if (m === 'application/vnd.google-apps.presentation') return { label: 'PowerPoint', ext: 'pptx' };
    return null;
  };

  const triggerDownload = (doc, format) => {
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`;
    const href = format ? `${base}?format=${format}` : base;
    const a = document.createElement('a');
    a.href = href;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success(`Preparing "${doc.title}"…`);
  };

  // "Open original in Drive" was removed on purpose: users must make their own copy.

  // Google-native copy link: opens Google's "Make a copy" dialog into the user's own Drive
  const driveEmailRef = useRef(null);
  const getDriveCopyUrl = (doc) => {
    if (!doc || !doc.drive_file_id) return null;
    const id = doc.drive_file_id;
    const m = doc.drive_mime_type || '';
    let base = null;
    if (m === 'application/vnd.google-apps.document') base = `https://docs.google.com/document/d/${id}/copy`;
    else if (m === 'application/vnd.google-apps.spreadsheet') base = `https://docs.google.com/spreadsheets/d/${id}/copy`;
    else if (m === 'application/vnd.google-apps.presentation') base = `https://docs.google.com/presentation/d/${id}/copy`;
    else if (m === 'application/vnd.google-apps.form') base = `https://docs.google.com/forms/d/${id}/copy`;
    if (base && driveEmailRef.current) return `${base}?authuser=${encodeURIComponent(driveEmailRef.current)}`;
    return base;
  };
  const makeCopyInDrive = async (doc) => {
    if (!user?.id) return;
    const connected = await ensureDriveConnected();
    if (!connected) { toast.error('Google Drive not connected. Please try again.'); return; }
    setCopyDestPrompt({ kind: 'doc', target: doc });
  };

  const doCopyDoc = async (doc, destId, destName) => {
    const tId = toast.loading(`Copying "${doc.title}" to your Drive\u2026`);
    try {
      const start = await axios.post(`${API}/drive/copy-file/${doc.id}`, null, { params: { requester_id: user.id, ...(destId ? { dest_folder_id: destId } : {}) } });
      const jobId = start.data.job_id;
      for (let i = 0; i < 200; i++) {
        await new Promise((r) => setTimeout(r, 1500));
        let job;
        try {
          job = (await axios.get(`${API}/drive/copy-job/${jobId}`)).data;
        } catch {
          continue; // transient poll hiccup — keep waiting, the copy continues server-side
        }
        if (job.status === 'complete') {
          toast.success('Copied to your Google Drive', {
            id: tId,
            action: job.webViewLink ? { label: 'Open', onClick: () => window.open(job.webViewLink, '_blank', 'noopener') } : undefined,
          });
          setCopyReceipt({
            title: doc.title,
            folderLink: null,
            files: [{ title: job.name || doc.title, link: job.webViewLink }],
            skipped: [],
            formsPending: [],
          });
          return;
        }
        if (job.status === 'failed') {
          if (job.error_code === 428) {
            toast.dismiss(tId);
            const reconnected = await ensureDriveConnected();
            if (reconnected) return doCopyDoc(doc, destId, destName);
            toast.error('Google Drive not connected. Click the button again to connect.');
            return;
          }
          toast.error(job.error || 'Copy failed', { id: tId });
          return;
        }
      }
      toast.error('This copy is taking unusually long — check your Google Drive in a minute; it may still finish.', { id: tId });
    } catch (err) {
      const code = err.response?.status;
      if (code === 428) {
        toast.dismiss(tId);
        const reconnected = await ensureDriveConnected();
        if (reconnected) return doCopyDoc(doc, destId, destName);
        toast.error('Google Drive not connected. Click the button again to connect.');
        return;
      }
      if (code === 422 && getDriveCopyUrl(doc)) {
        toast.dismiss(tId);
        window.open(getDriveCopyUrl(doc), '_blank', 'noopener');
        if (destId) {
          setFormCopyPrompt({ phase: 'move', links: [], folderName: destName || 'your chosen folder', destDriveFolderId: destId, expectedTitles: [doc.title] });
          toast.info('Google opened a "Make a copy" page — click Make a copy, then come back here to file it into your folder.', { duration: 8000 });
        } else {
          toast.info('Google opened a "Make a copy" page — click Make a copy to save it to your Drive.', { duration: 8000 });
        }
        return;
      }
      toast.error(
        err.response?.data?.detail || (err.response ? 'Copy failed' : 'Connection hiccup — the copy may still have completed. Check your Google Drive before retrying.'),
        { id: tId }
      );
    }
  };

  const ensureDriveConnected = async () => {
    try {
      const s = await axios.get(`${API}/oauth/drive/status`, { params: { requester_id: user?.id } });
      if (s.data.google_email) driveEmailRef.current = s.data.google_email;
      if (s.data.last_dest) setLastDest(s.data.last_dest);
      if (s.data.connected) return true;
    } catch (e) { /* fall through to connect */ }
    let authUrl;
    try {
      const a = await axios.get(`${API}/oauth/drive/authorize`, { params: { requester_id: user?.id } });
      authUrl = a.data.authorize_url;
    } catch (e) {
      toast.error('Google Drive copy is not configured.');
      return false;
    }
    let popup = window.open(authUrl, 'drive-connect', 'width=520,height=660');
    if (!popup) {
      // Popup was blocked: give the user a direct button (a real click lets it through)
      toast.info('Your browser blocked the Google sign-in window.', {
        duration: 60000,
        action: {
          label: 'Connect Drive',
          onClick: () => { popup = window.open(authUrl, 'drive-connect', 'width=520,height=660'); },
        },
      });
    }
    return await new Promise((resolve) => {
      let done = false;
      const cleanup = () => { done = true; window.removeEventListener('message', onMsg); clearInterval(iv); };
      const onMsg = (ev) => { if (ev.data === 'drive-connected') { cleanup(); resolve(true); } };
      window.addEventListener('message', onMsg);
      const iv = setInterval(async () => {
        try {
          const s2 = await axios.get(`${API}/oauth/drive/status`, { params: { requester_id: user?.id } });
          if (s2.data.google_email) driveEmailRef.current = s2.data.google_email;
          if (s2.data.connected) { cleanup(); resolve(true); return; }
        } catch (e) { /* ignore */ }
        if (popup && popup.closed && !done) { cleanup(); resolve(false); }
      }, 1500);
      setTimeout(() => { if (!done) { cleanup(); resolve(false); } }, 180000);
    });
  };

  const copyFolderToDrive = async (folder) => {
    if (!user?.id) return;
    const connected = await ensureDriveConnected();
    if (!connected) { toast.error('Google Drive not connected. Please try again.'); return; }
    setCopyDestPrompt({ kind: 'folder', target: folder });
  };

  const doCopyFolder = async (folder, destId) => {
    const tId = toast.loading(`Preparing to copy "${folder.name}"\u2026`);
    try {
      const res = await axios.post(`${API}/drive/copy-folder/${folder.id}`, null, { params: { requester_id: user.id, ...(destId ? { dest_folder_id: destId } : {}) } });
      const jobId = res.data.job_id;
      let polls = 0;
      const poll = setInterval(async () => {
        polls += 1;
        if (polls > 300) { clearInterval(poll); toast.error('Copy is taking longer than expected. Check your Google Drive shortly.', { id: tId }); return; }
        try {
          const j = await axios.get(`${API}/drive/copy-job/${jobId}`);
          const d = j.data;
          if (d.status === 'running' || d.status === 'queued') {
            toast.loading(`Copying "${folder.name}" \u2014 ${d.done}/${d.total || '\u2026'} files`, { id: tId });
          } else if (d.status === 'complete') {
            clearInterval(poll);
            toast.success(`"${folder.name}" copied to your Drive${d.errors ? ` (${d.errors} skipped)` : ''}`, {
              id: tId,
              duration: 8000,
              action: d.webViewLink ? { label: 'Open', onClick: () => window.open(d.webViewLink, '_blank', 'noopener') } : undefined,
            });
            const receipt = {
              title: folder.name,
              folderLink: d.webViewLink || null,
              files: d.copied_files || [],
              skipped: d.skipped_files || [],
              formsPending: (d.form_links || []).map((f) => f.title),
            };
            if (d.form_links && d.form_links.length) {
              pendingReceiptRef.current = receipt;
              setFormCopyPrompt({ folderName: folder.name, links: d.form_links, destDriveFolderId: d.dest_drive_folder_id || null, phase: 'copy', expectedTitles: (d.form_links || []).map((f) => f.title) });
            } else {
              setCopyReceipt(receipt);
            }
          } else if (d.status === 'failed') {
            clearInterval(poll);
            toast.error('Folder copy failed. Please try again.', { id: tId });
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (err) {
      if (err.response?.status === 428) {
        toast.dismiss(tId);
        const reconnected = await ensureDriveConnected();
        if (reconnected) return doCopyFolder(folder, destId);
        toast.error('Google Drive not connected. Click the button again to connect.');
        return;
      }
      toast.error(err.response?.data?.detail || 'Could not start the copy', { id: tId });
    }
  };

  // ---- Destination chooser + Google Picker ----
  const loadPickerApi = () => new Promise((resolve, reject) => {
    if (window.google?.picker) return resolve();
    const existing = document.getElementById('gapi-script');
    const onReady = () => window.gapi.load('picker', () => resolve());
    if (existing) {
      if (window.gapi) return onReady();
      existing.addEventListener('load', onReady);
      return;
    }
    const s = document.createElement('script');
    s.id = 'gapi-script';
    s.src = 'https://apis.google.com/js/api.js';
    s.onload = onReady;
    s.onerror = () => reject(new Error('Failed to load Google Picker'));
    document.body.appendChild(s);
  });

  const pickDriveFolder = async () => {
    let cfg;
    try {
      cfg = (await axios.get(`${API}/oauth/drive/picker-config`, { params: { requester_id: user.id } })).data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Folder picker is not available right now.');
      return undefined;
    }
    try {
      await loadPickerApi();
    } catch (e) {
      toast.error('Could not load the Google folder picker.');
      return undefined;
    }
    return new Promise((resolve) => {
      const g = window.google.picker;
      const view = new g.DocsView(g.ViewId.FOLDERS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setMimeTypes('application/vnd.google-apps.folder');
      const picker = new g.PickerBuilder()
        .setOAuthToken(cfg.access_token)
        .setDeveloperKey(cfg.api_key)
        .setAppId(cfg.app_id)
        .addView(view)
        .setTitle('Choose a folder in your Google Drive')
        .setCallback((data) => {
          if (data.action === g.Action.PICKED) {
            const doc = data.docs?.[0];
            resolve(doc ? { id: doc.id, name: doc.name || '' } : undefined);
          } else if (data.action === g.Action.CANCEL) resolve(undefined);
        })
        .build();
      picker.setVisible(true);
    });
  };

  const pickFormCopies = async () => {
    let cfg;
    try {
      cfg = (await axios.get(`${API}/oauth/drive/picker-config`, { params: { requester_id: user.id } })).data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Picker is not available right now.');
      return undefined;
    }
    try {
      await loadPickerApi();
    } catch (e) {
      toast.error('Could not load the Google picker.');
      return undefined;
    }
    return new Promise((resolve) => {
      const g = window.google.picker;
      const view = new g.DocsView(g.ViewId.FORMS).setOwnedByMe(true);
      const picker = new g.PickerBuilder()
        .setOAuthToken(cfg.access_token)
        .setDeveloperKey(cfg.api_key)
        .setAppId(cfg.app_id)
        .addView(view)
        .enableFeature(g.Feature.MULTISELECT_ENABLED)
        .setTitle('Select your new form copies (newest first)')
        .setCallback((data) => {
          if (data.action === g.Action.PICKED) resolve((data.docs || []).map((doc) => doc.id));
          else if (data.action === g.Action.CANCEL) resolve(undefined);
        })
        .build();
      picker.setVisible(true);
    });
  };

  const moveFormCopies = async () => {
    const prompt = formCopyPrompt;
    const ids = await pickFormCopies();
    if (!ids || !ids.length) return;
    const tId = toast.loading('Filing your form copies into the folder\u2026');
    try {
      const res = await axios.post(`${API}/drive/move-files`, {
        file_ids: ids,
        dest_folder_id: prompt.destDriveFolderId,
        expected_titles: prompt.expectedTitles || [],
      }, { params: { requester_id: user.id } });
      const { moved, total, skipped, moved_files: movedFiles } = res.data;
      if (moved > 0) {
        toast.success(`${moved} of ${total} form${total === 1 ? '' : 's'} moved into "${prompt.folderName}"`, { id: tId, duration: 8000 });
      } else {
        toast.dismiss(tId);
      }
      if (skipped?.length) {
        toast.warning(
          `${skipped.length} selected file${skipped.length === 1 ? ' was' : 's were'} NOT moved (only fresh copies of portal forms can be filed): ${skipped.map((s) => `"${s.name}" — ${s.reason}`).join('; ')}`,
          { duration: 12000 }
        );
      }
      if (moved > 0 || !skipped?.length) closeFormPrompt(movedFiles || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not move the copies', { id: tId });
    }
  };

  const startCopyTo = async (mode) => {
    const prompt = copyDestPrompt;
    setCopyDestPrompt(null);
    if (!prompt) return;
    let destId = null;
    let destName = null;
    if (mode === 'pick') {
      const picked = await pickDriveFolder();
      if (!picked) return; // cancelled or picker unavailable
      destId = picked.id;
      destName = picked.name;
      setLastDest(picked);
      axios.post(`${API}/oauth/drive/last-dest`, null, { params: { requester_id: user.id, folder_id: picked.id, folder_name: picked.name } }).catch(() => {});
    } else if (mode === 'last' && lastDest) {
      destId = lastDest.id;
      destName = lastDest.name;
    }
    if (prompt.kind === 'doc') await doCopyDoc(prompt.target, destId, destName);
    else await doCopyFolder(prompt.target, destId);
  };

  // Folder appearance registries
  const FOLDER_ICONS = {
    folder: Folder, folderOpen: FolderOpen, book: BookOpen, cap: GraduationCap,
    briefcase: Briefcase, home: Home, users: Users, heart: Heart, star: Star,
    file: FileText, video: Video, image: ImageIcon, wrench: Wrench, lightbulb: Lightbulb,
    target: Target, award: Award, calendar: Calendar, clipboard: ClipboardList,
    shield: Shield, package: Package,
  };

  // Domain color-coding for Units 1-6: second number of the folder prefix is the domain.
  //   X.1(.x) -> Work (orange clipboard) | X.2(.x) -> Home (green house) | X.3(.x) -> Community (blue hand)
  // Applies to domain subfolders (e.g. "1.1 Safety at Work") AND competency folders (e.g. "1.1.1 ...").
  const DOMAIN_CONFIG = {
    work: { color: '#e87722', icon: `${API}/static/icons/work.png?v=2`, label: 'Work' },
    home: { color: '#6cc24a', icon: `${API}/static/icons/home.png?v=2`, label: 'Home' },
    community: { color: '#3aa1c9', icon: `${API}/static/icons/community.png?v=2`, label: 'Community' },
  };
  const getDomainKind = (name) => {
    const m = (name || '').trim().match(/^(\d+)\.(\d+)(?:\.\d+)?\b/);
    if (!m) return null;
    const unit = parseInt(m[1], 10);
    const domain = parseInt(m[2], 10);
    if (unit < 1 || unit > 6) return null; // Only Units 1-6 use the Work/Home/Community domains
    if (domain === 1) return 'work';
    if (domain === 2) return 'home';
    if (domain === 3) return 'community';
    return null;
  };
  // Unit-level tile colors (Curriculum root) per the brand schema.
  const getUnitTileColor = (name) => {
    const t = (name || '').trim().toLowerCase();
    if (/^modules\b/.test(t)) return '#6cc24a';        // green (per schema)
    if (/orientation/.test(t)) return '#6cc24a';       // green (per schema)
    if (/^unit\s*1\b/.test(t)) return '#ef4444';       // red - Safety
    if (/^unit\s*2\b/.test(t)) return '#f97316';       // orange - Self-Determination
    if (/^unit\s*3\b/.test(t)) return '#eab308';       // yellow - Independence
    if (/^unit\s*4\b/.test(t)) return '#22c55e';       // green - Social & Communication
    if (/^unit\s*5\b/.test(t)) return '#3b82f6';       // blue - Financial
    if (/^unit\s*6\b/.test(t)) return '#6366f1';       // indigo - Technology
    if (/^unit\s*7\b/.test(t)) return '#a855f7';       // purple - Physical & Emotional
    if (/^unit\s*8\b/.test(t)) return '#ec4899';       // pink - Microenterprise
    return null;
  };
  const FOLDER_COLORS = [
    '#34657f', '#e87722', '#6cc24a', '#3aa1c9', '#ec4899', '#a855f7',
    '#ef4444', '#f59e0b', '#14b8a6', '#6366f1', '#64748b', '#0ea5e9',
  ];

  const openAppearanceDialog = (folder) => {
    setAppearanceTarget(folder);
    setAppearanceColor(folder.color || '#34657f');
    setAppearanceIcon(folder.icon || 'folder');
    setAppearanceOpen(true);
  };

  const handleSaveAppearance = async () => {
    if (!appearanceTarget) return;
    try {
      await axios.put(`${API}/folders/${appearanceTarget.id}/appearance`, {
        color: appearanceColor,
        icon: appearanceIcon,
      });
      toast.success('Folder appearance updated');
      setAppearanceOpen(false);
      setAppearanceTarget(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update appearance');
    }
  };

  const handleResetAppearance = async () => {
    if (!appearanceTarget) return;
    try {
      await axios.put(`${API}/folders/${appearanceTarget.id}/appearance`, { color: null, icon: null });
      toast.success('Folder appearance reset to default');
      setAppearanceOpen(false);
      setAppearanceTarget(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to reset appearance');
    }
  };

  const handleMoveFolderToSection = async (folderId, section) => {
    try {
      await axios.put(`${API}/folders/${folderId}/section`, { section });
      toast.success(`Moved to ${SECTION_LABELS[section]}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to move folder');
    }
  };

  // Document Card Component
  const DocumentCard = ({ doc, index }) => {
    const { icon: Icon, color } = getDocIcon(doc.doc_type);
    const isSelected = selectedItems.documents.includes(doc.id);
    const isDragging = draggedItem?.id === doc.id && draggedType === 'document';
    
    return (
      <Card 
        className={`dashboard-card group relative transition-all ${
          isSelected ? 'ring-2 ring-[#34657f]' : ''
        } ${(selectionMode || (!reorderMode && isPreviewable(doc))) ? 'cursor-pointer' : ''} ${
          isDragging ? 'opacity-50 scale-95' : ''
        } ${reorderMode ? 'cursor-grab active:cursor-grabbing' : ''}`}
        onClick={() => {
          if (selectionMode) {
            toggleDocumentSelection(doc.id);
          } else if (!reorderMode && isPreviewable(doc)) {
            setPreviewDoc(doc);
          }
        }}
        draggable={reorderMode}
        onDragStart={(e) => handleDragStart(e, doc, 'document')}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, doc, 'document')}
        onDragEnd={handleDragEnd}
        data-testid={`doc-card-${doc.id}`}
      >
        {reorderMode && (
          <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-500 hover:text-[#34657f] hover:bg-[#34657f]/10"
              onClick={(e) => { e.stopPropagation(); handleMoveDocument(doc.id, 'up'); }}
              disabled={index === 0}
              data-testid={`move-doc-up-${doc.id}`}
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-500 hover:text-[#34657f] hover:bg-[#34657f]/10"
              onClick={(e) => { e.stopPropagation(); handleMoveDocument(doc.id, 'down'); }}
              disabled={index === documents.length - 1}
              data-testid={`move-doc-down-${doc.id}`}
            >
              <ArrowDown className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        {selectionMode && !reorderMode && (
          <div className="absolute top-3 left-3 z-10">
            <Checkbox 
              checked={isSelected}
              onCheckedChange={() => toggleDocumentSelection(doc.id)}
              className="h-5 w-5"
            />
          </div>
        )}
        
        <CardContent className={`p-5 ${reorderMode || selectionMode ? 'pl-10' : ''}`}>
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-xl ${color} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
              <Icon className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base text-[#1A1A1A] mb-1 leading-snug break-words group-hover:text-[#34657f] transition-colors" title={doc.title}>
                {doc.title}
              </h3>
              <p className="text-sm text-[#5C5C5C] line-clamp-2 mb-2">
                {doc.description || 'No description'}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  const tb = getDocTypeBadge(doc.title);
                  return tb ? (
                    <Badge className={`${tb.className} text-xs`} data-testid={`doc-type-badge-${doc.id}`}>
                      {tb.label}
                    </Badge>
                  ) : null;
                })()}
                {doc.content_type && doc.content_type !== 'other' && (
                  <Badge variant="secondary" className="badge-blue text-xs capitalize">
                    {doc.content_type}
                  </Badge>
                )}
                {doc.file_size && (
                  <span className="text-xs text-[#5C5C5C]">{formatFileSize(doc.file_size)}</span>
                )}
              </div>
              {(() => {
                const sync = getDriveSyncState(doc);
                if (!sync) return null;
                return (
                  <div
                    className="flex items-center gap-1.5 mt-2 text-xs text-[#5C5C5C]"
                    title={sync.tooltip}
                    data-testid={`drive-sync-${doc.id}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${sync.dotColor} shrink-0`} />
                    <span className="min-w-0">{sync.label}</span>
                    {!isDemo && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); handleSyncDocument(doc.id); }}
                        disabled={syncingDocs.has(doc.id)}
                        className="ml-1 inline-flex items-center text-[#34657f] hover:bg-[#34657f]/10 rounded p-0.5 disabled:opacity-50"
                        title="Refresh from Drive"
                        data-testid={`drive-refresh-${doc.id}`}
                      >
                        <RefreshCw className={`w-3 h-3 ${syncingDocs.has(doc.id) ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
            
            {!selectionMode && !reorderMode && (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" aria-label="Document options" className="shrink-0" data-testid={`doc-more-${doc.id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  {isPreviewable(doc) && (
                    <DropdownMenuItem onClick={() => setPreviewDoc(doc)} data-testid={`menu-preview-${doc.id}`}>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </DropdownMenuItem>
                  )}
                  {!isDemo && (() => {
                    const kind = getDriveNativeKind(doc);
                    if (doc.drive_file_id) {
                      return (
                        <>
                          {kind ? (
                            <>
                              <DropdownMenuItem onClick={() => triggerDownload(doc, 'pdf')} data-testid={`menu-download-pdf-${doc.id}`}>
                                <Download className="w-4 h-4 mr-2" />
                                Download as PDF
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => triggerDownload(doc, 'editable')} data-testid={`menu-download-editable-${doc.id}`}>
                                <Download className="w-4 h-4 mr-2" />
                                Download as {kind.label}
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem onClick={() => triggerDownload(doc, 'original')} data-testid={`menu-download-${doc.id}`}>
                              <Download className="w-4 h-4 mr-2" />
                              Download file
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => makeCopyInDrive(doc)} data-testid={`menu-copy-drive-${doc.id}`} className="text-[#b45309] font-medium">
                            <Copy className="w-4 h-4 mr-2" />
                            Make a copy in my Drive
                          </DropdownMenuItem>
                        </>
                      );
                    }
                    if (doc.file_url) {
                      return (
                        <DropdownMenuItem onClick={() => triggerDownload(doc, 'original')} data-testid={`menu-download-${doc.id}`}>
                          <Download className="w-4 h-4 mr-2" />
                          Download original file
                        </DropdownMenuItem>
                      );
                    }
                    return null;
                  })()}
                  {user?.role === 'system_admin' && (
                    <>
                      <DropdownMenuSeparator />
                      {(doc.drive_status === 'missing' || doc.drive_status === 'trashed') && (
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => {
                            setDeleteTarget(doc);
                            setDeleteType('document');
                          }}
                          data-testid={`remove-from-portal-${doc.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove from portal (deleted in Drive)
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => {
                          setDeleteTarget(doc);
                          setDeleteType('document');
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          
          {!selectionMode && !reorderMode && (doc.file_url || doc.drive_file_id) && (
            <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-slate-100 overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-wrap items-center gap-1.5">
                {isPreviewable(doc) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 px-2 text-[#34657f] hover:bg-[#34657f]/10"
                    onClick={() => setPreviewDoc(doc)}
                    data-testid={`preview-${doc.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Preview
                  </Button>
                )}
                {!isDemo && (() => {
                  const kind = getDriveNativeKind(doc);
                  if (kind) {
                    return (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-1 px-2 text-[#3d7a22] hover:bg-[#6cc24a]/10"
                          onClick={() => triggerDownload(doc, 'pdf')}
                          data-testid={`download-pdf-${doc.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex-1 px-2 text-[#34657f] hover:bg-[#34657f]/10"
                          onClick={() => triggerDownload(doc, 'editable')}
                          data-testid={`download-editable-${doc.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          {kind.label}
                        </Button>
                      </>
                    );
                  }
                  return (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex-1 text-[#3d7a22] hover:bg-[#6cc24a]/10"
                      onClick={() => triggerDownload(doc, 'original')}
                      data-testid={`download-${doc.id}`}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  );
                })()}
              </div>
              {!isDemo && doc.drive_file_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full px-2 h-auto py-2 whitespace-normal leading-tight text-[#b45309] font-medium hover:bg-[#e87722]/10 border border-[#e87722]/40"
                  onClick={() => makeCopyInDrive(doc)}
                  title="Make your own editable copy in your Google Drive"
                  data-testid={`copy-drive-${doc.id}`}
                >
                  <Copy className="w-4 h-4 mr-1 shrink-0" />
                  Make a copy in my Drive
                </Button>
              )}
            </div>
          )}
          
          {!doc.file_url && !doc.drive_file_id && !selectionMode && !reorderMode && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-xs text-center text-[#5C5C5C]">No file attached</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Folder Card Component
  const FolderCard = ({ folder, index }) => {
    const { icon: Icon, color } = getFolderIcon(folder.folder_type);
    // Unit 7 (Physical & Emotional Well-Being): override domain icons with plain colored folders
    const folderName = (folder.name || '').trim();
    const unit7Color = /^7\.1\b/.test(folderName) ? 'bg-pink-400'
      : /^7\.2\b/.test(folderName) ? 'bg-purple-400'
      : null;
    const domainKind = getDomainKind(folderName);
    const unitColor = getUnitTileColor(folderName);
    const unit7Hex = /^7\.1\b/.test(folderName) ? '#f472b6' : /^7\.2\b/.test(folderName) ? '#a855f7' : null;
    // Every tile gets a color outline. Priority: custom > domain > unit > unit7 > stable palette by id.
    const hashColor = (str) => {
      let h = 0;
      const s = String(str || '');
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
      return FOLDER_COLORS[h % FOLDER_COLORS.length];
    };
    const tileColor = folder.color
      || (domainKind ? DOMAIN_CONFIG[domainKind].color : null)
      || unitColor || unit7Hex
      || hashColor(folder.id || folderName);
    const isSelected = selectedItems.folders.includes(folder.id);
    const isDragging = draggedItem?.id === folder.id && draggedType === 'folder';
    
    return (
      <Card 
        className={`dashboard-card cursor-pointer group relative transition-all ${
          isSelected ? 'ring-2 ring-[#34657f]' : ''
        } ${isDragging ? 'opacity-50 scale-95' : ''} ${
          reorderMode ? 'cursor-grab active:cursor-grabbing' : ''
        }`}
        onClick={() => {
          if (selectionMode) {
            toggleFolderSelection(folder.id);
          } else if (!reorderMode) {
            handleFolderClick(folder.id);
          }
        }}
        draggable={reorderMode || isAdmin}
        onDragStart={(e) => handleDragStart(e, folder, 'folder')}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, folder, 'folder')}
        onDragEnd={handleDragEnd}
        data-testid={`folder-card-${folder.id}`}
        style={{ borderColor: tileColor, borderWidth: '2px' }}
        title={isAdmin && !reorderMode ? 'Tip: drag onto a section in the left menu to reassign' : undefined}
      >
        {reorderMode && (
          <div className="absolute top-2 left-2 z-10 flex flex-col gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-500 hover:text-[#34657f] hover:bg-[#34657f]/10"
              onClick={(e) => { e.stopPropagation(); handleMoveFolder(folder.id, 'up'); }}
              disabled={index === 0}
              data-testid={`move-folder-up-${folder.id}`}
            >
              <ArrowUp className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-500 hover:text-[#34657f] hover:bg-[#34657f]/10"
              onClick={(e) => { e.stopPropagation(); handleMoveFolder(folder.id, 'down'); }}
              disabled={index === folders.length - 1}
              data-testid={`move-folder-down-${folder.id}`}
            >
              <ArrowDown className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        {selectionMode && !reorderMode && (
          <div className="absolute top-3 left-3 z-10">
            <Checkbox 
              checked={isSelected}
              onCheckedChange={() => toggleFolderSelection(folder.id)}
              onClick={(e) => e.stopPropagation()}
              className="h-5 w-5"
            />
          </div>
        )}
        
        <CardContent className={`p-5 ${reorderMode || selectionMode ? 'pl-10' : ''}`}>
          <div className="flex flex-col items-center text-center">
            {(folder.color || folder.icon) ? (
              (() => {
                const CustomIcon = FOLDER_ICONS[folder.icon] || Folder;
                return (
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: folder.color || '#34657f' }}
                  >
                    <CustomIcon className="w-6 h-6 text-white" />
                  </div>
                );
              })()
            ) : domainKind ? (
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: `${DOMAIN_CONFIG[domainKind].color}22` }}
              >
                <img
                  src={DOMAIN_CONFIG[domainKind].icon}
                  alt={DOMAIN_CONFIG[domainKind].label}
                  className="w-8 h-8 object-contain"
                />
              </div>
            ) : unitColor ? (
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: unitColor }}
              >
                <Folder className="w-6 h-6 text-white" />
              </div>
            ) : unit7Color ? (
              <div className={`w-12 h-12 rounded-xl ${unit7Color} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`}>
                <Folder className="w-6 h-6 text-white" />
              </div>
            ) : folder.thumbnail_url ? (
              <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 group-hover:scale-110 transition-transform">
                <img 
                  src={folder.thumbnail_url} 
                  alt={folder.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform" style={{ backgroundColor: tileColor }}>
                <Icon className="w-6 h-6 text-white" />
              </div>
            )}
            <div className="mt-3 w-full min-w-0">
              <h3
                className="font-semibold text-base text-[#1A1A1A] group-hover:text-[#34657f] transition-colors leading-snug break-words min-h-[2.75rem]"
                title={folder.name}
              >
                {folder.name}
              </h3>
              {folder.description && (
                <p className="text-sm text-[#5C5C5C] mt-1 line-clamp-2">
                  {folder.description}
                </p>
              )}
              <Badge variant="secondary" className="mt-2 text-xs capitalize">
                {folder.folder_type}
              </Badge>
              {folder.hidden && (
                <Badge className="mt-1 ml-1 text-[10px] bg-slate-700 text-white hover:bg-slate-700" data-testid={`hidden-badge-${folder.id}`}>
                  Hidden · Admins only
                </Badge>
              )}
            </div>

            {!selectionMode && !reorderMode && (
              <div className="w-full mt-4 pt-3 border-t border-slate-100 flex flex-col gap-2 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 px-2 text-[#34657f] hover:bg-[#34657f]/10"
                    onClick={() => navigateToFolder(folder.id)}
                    data-testid={`open-folder-btn-${folder.id}`}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Open
                  </Button>
                  {!isDemo && (
                  <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 px-2 text-[#3d7a22] hover:bg-[#6cc24a]/10"
                    onClick={() => downloadFolder(folder.id, folder.name, 'pdf')}
                    title="Download everything in this folder as PDFs"
                    data-testid={`folder-tile-pdf-${folder.id}`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 px-2 text-[#34657f] hover:bg-[#34657f]/10"
                    onClick={() => downloadFolder(folder.id, folder.name, 'office')}
                    title="Download everything in this folder as Microsoft files"
                    data-testid={`folder-tile-office-${folder.id}`}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Microsoft
                  </Button>
                  </>
                  )}
                </div>
                {!isDemo && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full px-2 h-auto py-2 whitespace-normal leading-tight text-[#b45309] font-medium hover:bg-[#e87722]/10 border border-[#e87722]/40"
                  onClick={() => copyFolderToDrive(folder)}
                  title="Copy this whole folder into your own Google Drive"
                  data-testid={`copy-folder-drive-btn-${folder.id}`}
                >
                  <Copy className="w-4 h-4 mr-1 shrink-0" />
                  Make a copy in my Drive
                </Button>
                )}
              </div>
            )}
            
            {!selectionMode && !reorderMode && (
              <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" aria-label="Folder options" className="shrink-0 h-8 w-8" data-testid={`folder-menu-${folder.id}`}>
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    handleFolderClick(folder.id);
                  }}>
                    <FolderOpen className="w-4 h-4 mr-2" />
                    Open
                  </DropdownMenuItem>
                  {!isDemo && (
                  <>
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    copyFolderToDrive(folder);
                  }} data-testid={`copy-folder-drive-${folder.id}`} className="text-[#b45309] font-medium">
                    <Copy className="w-4 h-4 mr-2" />
                    Make a copy in my Drive
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    openDownloadConfirm(folder, 'office');
                  }} data-testid={`download-folder-office-${folder.id}`}>
                    <Download className="w-4 h-4 mr-2" />
                    Download as Microsoft files
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    openDownloadConfirm(folder, 'pdf');
                  }} data-testid={`download-folder-pdf-${folder.id}`}>
                    <Download className="w-4 h-4 mr-2" />
                    Download as PDF
                  </DropdownMenuItem>
                  </>
                  )}
                  {folder.drive_folder_id && (user?.role === 'system_admin' || user?.role === 'team_admin') && (
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      resyncFolder(folder);
                    }} disabled={resyncingFolderId === folder.id} data-testid={`resync-folder-${folder.id}`}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${resyncingFolderId === folder.id ? 'animate-spin' : ''}`} />
                      Re-sync from Drive
                    </DropdownMenuItem>
                  )}
                  {(user?.role === 'system_admin' || user?.role === 'team_admin') && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          setRenameFolderTarget(folder);
                          setRenameFolderValue(folder.name);
                          setRenameFolderOpen(true);
                        }}
                        data-testid={`rename-folder-${folder.id}`}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Rename
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          openAppearanceDialog(folder);
                        }}
                        data-testid={`customize-folder-${folder.id}`}
                      >
                        <Palette className="w-4 h-4 mr-2" />
                        Customize appearance
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-[#5C5C5C]">Move to section</DropdownMenuLabel>
                      {Object.entries(SECTION_LABELS)
                        .filter(([key]) => key !== folder.section)
                        .map(([key, label]) => (
                          <DropdownMenuItem
                            key={key}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveFolderToSection(folder.id, key);
                            }}
                            data-testid={`move-section-${key}-${folder.id}`}
                          >
                            <ChevronRight className="w-4 h-4 mr-2" />
                            {label}
                          </DropdownMenuItem>
                        ))}
                    </>
                  )}
                  {user?.role === 'system_admin' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(folder);
                          setDeleteType('folder');
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="curriculum-loading">
        <div className="w-8 h-8 border-4 border-[#34657f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="curriculum-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]" data-testid="curriculum-heading">
            {currentSection ? SECTION_LABELS[currentSection] : 'Curriculum & Toolkit'}
          </h1>
          <p className="text-[#5C5C5C] mt-2">
            {currentSection && !currentFolder
              ? `Folders in ${SECTION_LABELS[currentSection]}`
              : 'Browse, upload, and download learning materials'}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap sm:justify-end">
          {isAdmin && folderHasDriveDocs && (
            <Button
              variant="outline"
              onClick={handleManualSyncFolder}
              disabled={syncingFolder}
              data-testid="sync-drive-btn"
              title="Re-check this folder's Drive files for updates"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncingFolder ? 'animate-spin' : ''}`} />
              {syncingFolder ? 'Syncing…' : 'Sync Drive'}
            </Button>
          )}
          {isAdmin && (
            <>
              <Button
                variant="outline"
                onClick={openDriveDialog}
                data-testid="import-drive-btn"
              >
                <Cloud className="w-4 h-4 mr-2" />
                Import from Drive
              </Button>
              <Button
                variant="outline"
                onClick={() => setCreateFolderOpen(true)}
                data-testid="create-folder-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Folder
              </Button>
              <Button
                className="bg-[#b34e00] hover:bg-[#a84d09]"
                onClick={() => setUploadOpen(true)}
                data-testid="upload-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
              {isAdmin && currentSection && (
                <Button
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={() => setClearSectionOpen(true)}
                  data-testid="clear-section-btn"
                  title="Remove all folders and files from this section (use before re-syncing Drive)"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear section
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Work-Based Learning "Coming Soon" banner */}
      {currentSection === 'work_based_learning' && (
        <div
          className="flex items-center gap-4 rounded-xl border border-[#e87722]/30 bg-[#e87722]/10 px-6 py-5"
          data-testid="wbl-coming-soon-banner"
        >
          <div className="w-10 h-10 rounded-lg bg-[#b34e00] flex items-center justify-center shrink-0">
            <Wrench className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="font-semibold text-[#1A1A1A]">Coming Soon</p>
            <p className="text-sm text-[#5C5C5C]">Job Site Data Reporting functionality is coming soon.</p>
          </div>
        </div>
      )}

      {/* Breadcrumb Navigation */}
      <div className="flex flex-wrap items-center gap-y-1 gap-x-1 text-sm">
        <Button
          variant="ghost"
          size="sm"
          aria-label="Curriculum home"
          className="text-[#34657f] px-2"
          onClick={() => navigateToRoot()}
        >
          <Home className="w-4 h-4" />
        </Button>
        {currentSection && (
          <>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <Button
              variant="ghost"
              size="sm"
              className="text-[#34657f] px-2 font-medium"
              onClick={() => setSearchParams({ section: currentSection })}
            >
              {SECTION_LABELS[currentSection]}
            </Button>
          </>
        )}
        {breadcrumb.map((item, index) => (
          <React.Fragment key={item.id}>
            <ChevronRight className="w-4 h-4 text-slate-400" />
            <Button
              variant="ghost"
              size="sm"
              className={`px-2 max-w-[240px] truncate ${index === breadcrumb.length - 1 ? 'font-semibold text-[#1A1A1A]' : 'text-[#5C5C5C]'}`}
              onClick={() => handleBreadcrumbClick(item.id)}
              title={item.name}
            >
              {item.name}
            </Button>
          </React.Fragment>
        ))}
      </div>

      {/* Search, Filters, and Selection Bar */}
      <Card className="dashboard-card" data-testid="search-filters">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#34657f]" aria-hidden="true" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={currentSection ? `Search ${SECTION_LABELS[currentSection].toLowerCase()} folders and files…` : 'Search folders and files…'}
                  className="pl-10 h-12"
                  aria-label={currentSection ? `Search all ${SECTION_LABELS[currentSection]} folders and files` : 'Search folders and files'}
                  data-testid="curriculum-search-input"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-48 h-12" aria-label="Filter by file type" data-testid="filter-select">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="pdf">PDF Documents</SelectItem>
                  <SelectItem value="video">Videos</SelectItem>
                  <SelectItem value="document">Word Documents</SelectItem>
                  <SelectItem value="presentation">Presentations</SelectItem>
                  <SelectItem value="image">Images</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="icon"
                  aria-label="Grid view"
                  className={viewMode === 'grid' ? 'bg-[#34657f]' : ''}
                  onClick={() => setViewMode('grid')}
                  data-testid="grid-view-btn"
                >
                  <Grid className="w-4 h-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="icon"
                  aria-label="List view"
                  className={viewMode === 'list' ? 'bg-[#34657f]' : ''}
                  onClick={() => setViewMode('list')}
                  data-testid="list-view-btn"
                >
                  <List className="w-4 h-4" />
                </Button>
                {!isDemo && (
                <Button
                  variant={selectionMode ? 'default' : 'outline'}
                  size="icon"
                  aria-label="Select multiple items"
                  className={selectionMode ? 'bg-[#3d7a22]' : ''}
                  onClick={() => {
                    setSelectionMode(!selectionMode);
                    setReorderMode(false);
                  }}
                  data-testid="selection-mode-btn"
                  title="Select multiple items"
                >
                  {selectionMode ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </Button>
                )}
                {(user?.role === 'system_admin' || user?.role === 'team_admin') && (
                <Button
                  variant={reorderMode ? 'default' : 'outline'}
                  size="icon"
                  aria-label="Reorder items"
                  className={reorderMode ? 'bg-[#b34e00] text-white' : ''}
                  onClick={() => {
                    setReorderMode(!reorderMode);
                    setSelectionMode(false);
                  }}
                  data-testid="reorder-mode-btn"
                  title="Drag to reorder"
                >
                  <GripVertical className="w-4 h-4" />
                </Button>
                )}
              </div>
            </div>
            
            {/* Selection Actions */}
            {selectionMode && (
              <div className="flex items-center justify-between p-3 bg-[#34657f]/5 rounded-lg">
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-[#34657f]">
                    {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
                  </span>
                  <Button variant="ghost" size="sm" onClick={selectAll}>
                    Select All
                  </Button>
                  <Button variant="ghost" size="sm" onClick={clearSelection}>
                    Clear
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="bg-[#34657f]"
                    onClick={() => downloadMultiple('office')}
                    disabled={selectedItems.documents.length === 0}
                    data-testid="download-selected-office-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Microsoft files
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#34657f] text-[#34657f]"
                    onClick={() => downloadMultiple('pdf')}
                    disabled={selectedItems.documents.length === 0}
                    data-testid="download-selected-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSelection}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            
            {/* Reorder Mode Indicator */}
            {reorderMode && (
              <div className="flex items-center justify-between p-3 bg-[#e87722]/10 rounded-lg">
                <div className="flex items-center gap-2">
                  <GripVertical className="w-5 h-5 text-[#b34e00]" />
                  <span className="text-sm font-medium text-[#9A6700]">
                    Drag and drop to reorder items
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReorderMode(false)}
                >
                  Done
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recently Added strip (curriculum root only) */}
      {!currentFolder && !searchQuery && recentDocs.length > 0 && (
        <div data-testid="recently-added-strip">
          <h2 className="text-xl font-bold font-['Manrope'] text-[#1A1A1A] mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#1f7fa6]" />
            Recently Added
            <span className="text-sm font-normal text-[#5C5C5C]">({recentDocs.length})</span>
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {recentDocs.map((doc) => {
              const { icon: Icon, color } = getDocIcon(doc.doc_type);
              return (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => (isPreviewable(doc) ? setPreviewDoc(doc) : triggerDownload(doc, getDriveNativeKind(doc) ? 'pdf' : 'original'))}
                  className="shrink-0 w-60 text-left bg-white border border-slate-200 rounded-xl p-4 hover:border-[#34657f]/40 hover:shadow-md transition-all"
                  data-testid={`recent-doc-${doc.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center shrink-0`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1A1A1A] truncate" title={doc.title}>{doc.title}</p>
                      <p className="text-xs text-[#5C5C5C]">Added {formatRelativeTime(doc.created_at)}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Folders Section */}
      {/* Deep search indicator */}
      {deepResults && (
        <div className="flex items-center gap-2 rounded-lg bg-[#34657f]/5 border border-[#34657f]/20 px-4 py-2.5 text-sm text-[#34657f]" data-testid="deep-search-banner">
          <Search className="w-4 h-4 shrink-0" />
          <span>
            Showing matches from <strong>all of {SECTION_LABELS[currentSection]}</strong> — {displayFolders.length} folder{displayFolders.length !== 1 ? 's' : ''}, {displayDocuments.length} file{displayDocuments.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            className="ml-auto underline underline-offset-2 font-semibold hover:text-[#b34e00]"
            onClick={() => { setSearchQuery(''); setFilterType('all'); }}
            data-testid="clear-deep-search-btn"
          >
            Clear
          </button>
        </div>
      )}

      {displayFolders.length > 0 && (
        <div>
          <h2 className="mb-5 flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-[#e87722]/10 flex items-center justify-center shrink-0">
              <FolderOpen className="w-5 h-5 text-[#b34e00]" />
            </span>
            <span className="text-xl font-bold font-['Manrope'] text-[#1A1A1A] tracking-[0.01em]">Folders</span>
            <span className="text-sm font-semibold text-[#5C5C5C] bg-slate-100 rounded-full px-2.5 py-0.5 leading-none">{displayFolders.length}</span>
          </h2>
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4' 
            : 'space-y-3'
          }>
            {displayFolders.map((folder, index) => (
              <FolderCard key={folder.id} folder={folder} index={index} />
            ))}
          </div>
        </div>
      )}

      {/* Documents Section */}
      <div>
        <h2 className="mb-5 flex items-center gap-3">
          <span className="w-9 h-9 rounded-lg bg-[#34657f]/10 flex items-center justify-center shrink-0">
            <FileText className="w-5 h-5 text-[#34657f]" />
          </span>
          <span className="text-xl font-bold font-['Manrope'] text-[#1A1A1A] tracking-[0.01em]">Documents</span>
          <span className="text-sm font-semibold text-[#5C5C5C] bg-slate-100 rounded-full px-2.5 py-0.5 leading-none">{displayDocuments.length}</span>
        </h2>
        
        {displayDocuments.length > 0 ? (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4' 
            : 'space-y-3'
          }>
            {displayDocuments.map((doc, index) => (
              <DocumentCard key={doc.id} doc={doc} index={index} />
            ))}
          </div>
        ) : (
          <Card className="dashboard-card">
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                No documents found
              </h3>
              <p className="text-[#5C5C5C] mb-4">
                {searchQuery 
                  ? 'Try adjusting your search or filters' 
                  : isAdmin ? 'Upload documents or import from Google Drive' : 'No documents in this folder.'}
              </p>
              {isAdmin && (
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => setUploadOpen(true)} className="bg-[#b34e00]">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Document
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={(open) => {
        setUploadOpen(open);
        if (!open) { setUploadMode('single'); setUploadFiles([]); setUploadFile(null); }
      }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Upload Documents</DialogTitle>
            <DialogDescription>
              Upload one or multiple files to the curriculum library.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {/* Upload Mode Toggle */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
              <button
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  uploadMode === 'single' ? 'bg-white shadow text-[#34657f]' : 'text-[#5C5C5C] hover:text-[#1A1A1A]'
                }`}
                onClick={() => { setUploadMode('single'); setUploadFiles([]); }}
                data-testid="upload-mode-single"
              >
                Single File
              </button>
              <button
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  uploadMode === 'bulk' ? 'bg-white shadow text-[#34657f]' : 'text-[#5C5C5C] hover:text-[#1A1A1A]'
                }`}
                onClick={() => { setUploadMode('bulk'); setUploadFile(null); }}
                data-testid="upload-mode-bulk"
              >
                Bulk Upload
              </button>
            </div>

            {uploadMode === 'bulk' ? (
              <>
                <div className="space-y-2">
                  <Label>Files</Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#34657f] transition-colors">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setUploadFiles(prev => [...prev, ...files]);
                      }}
                      className="hidden"
                      id="bulk-file-upload"
                      data-testid="bulk-file-input"
                    />
                    <label htmlFor="bulk-file-upload" className="cursor-pointer">
                      <Upload className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                      <p className="text-sm text-[#5C5C5C]">
                        Click to select multiple files
                      </p>
                      <p className="text-xs text-[#8C8C8C] mt-1">
                        PDFs, images, documents, presentations, videos
                      </p>
                    </label>
                  </div>
                </div>

                {uploadFiles.length > 0 && (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    <Label>{uploadFiles.length} file{uploadFiles.length !== 1 ? 's' : ''} selected</Label>
                    {uploadFiles.map((file, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <FileText className="w-4 h-4 text-[#34657f] shrink-0" />
                        <span className="text-sm text-[#1A1A1A] flex-1 truncate">{file.name}</span>
                        <span className="text-xs text-[#5C5C5C] shrink-0">{formatFileSize(file.size)}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 shrink-0"
                          onClick={() => setUploadFiles(prev => prev.filter((_, idx) => idx !== i))}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Content Type (applied to all)</Label>
                  <Select 
                    value={uploadData.content_type} 
                    onValueChange={(v) => setUploadData(prev => ({ ...prev, content_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unit">Unit</SelectItem>
                      <SelectItem value="lesson">Lesson</SelectItem>
                      <SelectItem value="activity">Activity</SelectItem>
                      <SelectItem value="module">Module</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>File</Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#34657f] transition-colors">
                    <input
                      type="file"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setUploadFile(file);
                          if (!uploadData.title) {
                            setUploadData(prev => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
                          }
                        }
                      }}
                      className="hidden"
                      id="file-upload"
                      data-testid="file-input"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {uploadFile ? (
                        <div className="flex items-center justify-center gap-2">
                          <FileText className="w-8 h-8 text-[#34657f]" />
                          <div className="text-left">
                            <p className="font-medium text-[#1A1A1A]">{uploadFile.name}</p>
                            <p className="text-sm text-[#5C5C5C]">{formatFileSize(uploadFile.size)}</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                          <p className="text-sm text-[#5C5C5C]">
                            Click to select a file
                          </p>
                        </>
                      )}
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={uploadData.title}
                    onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Document title"
                    data-testid="upload-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={uploadData.description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description"
                    rows={2}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Content Type</Label>
                    <Select 
                      value={uploadData.content_type} 
                      onValueChange={(v) => setUploadData(prev => ({ ...prev, content_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unit">Unit</SelectItem>
                        <SelectItem value="lesson">Lesson</SelectItem>
                        <SelectItem value="activity">Activity</SelectItem>
                        <SelectItem value="module">Module</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags (comma separated)</Label>
                    <Input
                      id="tags"
                      value={uploadData.tags}
                      onChange={(e) => setUploadData(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder="safety, unit1"
                    />
                  </div>
                </div>
              </>
            )}
            
            {isUploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Uploading{uploadMode === 'bulk' ? ` ${uploadFiles.length} files` : ''}...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={uploadMode === 'bulk' ? handleBulkUpload : handleUpload} 
              disabled={isUploading || (uploadMode === 'single' ? !uploadFile : uploadFiles.length === 0)}
              className="bg-[#34657f]"
              data-testid="confirm-upload-btn"
            >
              {isUploading ? 'Uploading...' : uploadMode === 'bulk' ? `Upload ${uploadFiles.length} Files` : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear Section confirmation */}
      <AlertDialog open={clearSectionOpen} onOpenChange={(open) => { setClearSectionOpen(open); if (!open) setClearConfirmText(''); }}>
        <AlertDialogContent data-testid="clear-section-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Clear all content in {currentSection ? SECTION_LABELS[currentSection] : 'this section'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <strong>every folder and file</strong> in the{' '}
              <strong>{currentSection ? SECTION_LABELS[currentSection] : ''}</strong> section — including all
              Google Drive–synced items. Use this to wipe a messy sync so you can re-import cleanly. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="clear-section-input" className="text-sm text-[#5C5C5C]">
              To confirm, type the section name <strong className="text-[#1A1A1A]">{currentSection ? SECTION_LABELS[currentSection] : ''}</strong> below:
            </Label>
            <Input
              id="clear-section-input"
              value={clearConfirmText}
              onChange={(e) => setClearConfirmText(e.target.value)}
              placeholder={currentSection ? SECTION_LABELS[currentSection] : ''}
              autoComplete="off"
              className="mt-2"
              data-testid="clear-section-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearingSection} data-testid="clear-section-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); clearSectionContent(); }}
              disabled={clearingSection || clearConfirmText.trim() !== (currentSection ? SECTION_LABELS[currentSection] : '')}
              className="bg-red-600 hover:bg-red-700"
              data-testid="clear-section-confirm"
            >
              {clearingSection ? 'Clearing…' : 'Yes, clear everything'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Google Drive Import Dialog */}
      <Dialog open={driveOpen} onOpenChange={(open) => {
        setDriveOpen(open);
        if (!open) { setDriveUrl(''); setDrivePreview(null); }
      }}>
        <DialogContent className="sm:max-w-2xl" data-testid="drive-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2">
              <Cloud className="w-5 h-5 text-[#34657f]" />
              Import from Google Drive
            </DialogTitle>
            <DialogDescription>
              Paste a Drive file or folder link. Folders are imported recursively with their full subfolder structure. Files are stored as Drive links (no copies on our servers).
            </DialogDescription>
          </DialogHeader>

          {!driveStatus.configured ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-semibold mb-1">Google Drive is not connected yet</p>
              <p>Ask a system admin to configure the <code className="px-1 py-0.5 bg-amber-100 rounded">GOOGLE_SERVICE_ACCOUNT_JSON</code> backend environment variable. Once set, share any Drive folder with the service-account email and you can import it here.</p>
            </div>
          ) : (
            <div className="space-y-4 pt-2">
              <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700">
                <span className="font-semibold">Service account:</span>{' '}
                <code className="text-slate-900">{driveStatus.service_account_email}</code>
                <p className="mt-1">Share any Drive folder/file with this email as <strong>Viewer</strong>, then paste the link below.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="drive-url">Drive URL or ID *</Label>
                <Input
                  id="drive-url"
                  value={driveUrl}
                  onChange={(e) => setDriveUrl(e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  data-testid="drive-url-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4 items-end">
                <div className="space-y-2">
                  <Label>Default content type</Label>
                  <Select value={driveContentType} onValueChange={setDriveContentType}>
                    <SelectTrigger data-testid="drive-content-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unit">Unit</SelectItem>
                      <SelectItem value="lesson">Lesson</SelectItem>
                      <SelectItem value="activity">Activity</SelectItem>
                      <SelectItem value="module">Module</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pb-2">
                  <Checkbox
                    id="drive-recursive"
                    checked={driveRecursive}
                    onCheckedChange={(v) => setDriveRecursive(!!v)}
                    data-testid="drive-recursive-checkbox"
                  />
                  <Label htmlFor="drive-recursive" className="cursor-pointer">
                    Include subfolders (recursive)
                  </Label>
                </div>
              </div>

              {(!drivePreview || drivePreview.is_folder) && (
                <div className="space-y-2">
                  <Label>Sync into left-menu section</Label>
                  <Select value={driveSection} onValueChange={setDriveSection}>
                    <SelectTrigger data-testid="drive-section-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="curriculum">Curriculum</SelectItem>
                      <SelectItem value="work_based_learning">Work-Based Learning</SelectItem>
                      <SelectItem value="toolkit">Toolkit Resources</SelectItem>
                      <SelectItem value="training_files">Training Files</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">The imported Drive folder will appear as a top-level folder under this section in the left menu.</p>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleDrivePreview}
                  disabled={driveLoadingPreview || !driveUrl.trim()}
                  data-testid="drive-preview-btn"
                >
                  {driveLoadingPreview ? 'Loading preview…' : 'Preview'}
                </Button>
                {drivePreview && (
                  <a
                    href={drivePreview.webViewLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#34657f] underline self-center"
                  >
                    Open in Drive <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>

              {drivePreview && (
                <div className="rounded-lg border border-slate-200 bg-white p-3 max-h-72 overflow-auto" data-testid="drive-preview-result">
                  <div className="flex items-center gap-2 font-semibold text-slate-800 mb-2">
                    {drivePreview.is_folder ? <Folder className="w-4 h-4 text-[#b34e00]" /> : <FileText className="w-4 h-4 text-slate-500" />}
                    <span>{drivePreview.name}</span>
                  </div>
                  {drivePreview.is_folder ? (
                    <>
                      <div className="text-xs text-slate-600 mb-2">
                        {drivePreview.folder_count} folder(s), {drivePreview.file_count} file(s)
                      </div>
                      <ul className="text-sm space-y-0.5">
                        {(drivePreview.tree || []).slice(0, 50).map((node) => (
                          <li key={node.id} className="flex items-center gap-1.5 text-slate-700">
                            {node.mimeType === 'application/vnd.google-apps.folder'
                              ? <Folder className="w-3.5 h-3.5 text-[#b34e00]" />
                              : <FileText className="w-3.5 h-3.5 text-slate-400" />}
                            <span className="truncate">{node.name}</span>
                            {node.children && node.children.length > 0 && (
                              <span className="text-xs text-slate-400 ml-1">({node.children.length})</span>
                            )}
                          </li>
                        ))}
                        {(drivePreview.tree || []).length > 50 && (
                          <li className="text-xs text-slate-500 italic">… and {drivePreview.tree.length - 50} more</li>
                        )}
                      </ul>
                    </>
                  ) : (
                    <div className="text-xs text-slate-600">Single file • {drivePreview.mimeType}</div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDriveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDriveImport}
              disabled={!driveStatus.configured || !drivePreview || driveImporting}
              className="bg-[#34657f]"
              data-testid="confirm-drive-import-btn"
            >
              {driveImporting ? 'Importing…' : drivePreview?.is_folder ? 'Import folder' : 'Import file'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Folder Dialog */}
      <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Create New Folder</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="folder-name">Folder Name *</Label>
              <Input
                id="folder-name"
                value={newFolderData.name}
                onChange={(e) => setNewFolderData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Unit 9 - New Topic"
                data-testid="folder-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="folder-desc">Description</Label>
              <Textarea
                id="folder-desc"
                value={newFolderData.description}
                onChange={(e) => setNewFolderData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description"
                rows={2}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Folder Type</Label>
              <Select 
                value={newFolderData.folder_type} 
                onValueChange={(v) => setNewFolderData(prev => ({ ...prev, folder_type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unit">Unit</SelectItem>
                  <SelectItem value="lesson">Lesson</SelectItem>
                  <SelectItem value="activity">Activity</SelectItem>
                  <SelectItem value="module">Module</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateFolder}
              className="bg-[#34657f]"
              data-testid="confirm-create-folder-btn"
            >
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Download Confirmation */}
      <Dialog open={downloadConfirm.open} onOpenChange={(open) => setDownloadConfirm((prev) => ({ ...prev, open }))}>
        <DialogContent data-testid="download-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">
              Download {downloadConfirm.format === 'office' ? 'as Microsoft files' : 'as PDF'}
            </DialogTitle>
            <DialogDescription>
              {downloadConfirm.loading
                ? 'Counting files…'
                : downloadConfirm.count === null
                  ? (
                    <>
                      You're about to download all files from
                      {' '}<span className="font-semibold text-[#1A1A1A]">{downloadConfirm.folder?.name}</span> (including any subfolders).
                    </>
                  )
                  : (
                    <>
                      You're about to download <span className="font-semibold text-[#1A1A1A]">{downloadConfirm.count}</span> file{downloadConfirm.count === 1 ? '' : 's'} from
                      {' '}<span className="font-semibold text-[#1A1A1A]">{downloadConfirm.folder?.name}</span> (including any subfolders).
                    </>
                  )}
            </DialogDescription>
          </DialogHeader>
          {!downloadConfirm.loading && downloadConfirm.count !== null && downloadConfirm.count >= 50 && (
            <div className="flex items-start gap-3 rounded-lg border border-[#e87722]/30 bg-[#e87722]/10 px-4 py-3 text-sm text-[#5C5C5C]" data-testid="download-large-warning">
              <ExternalLink className="w-4 h-4 text-[#b34e00] mt-0.5 shrink-0" />
              <span>That's a large download and may take a while. If you only need part of it, download a specific subfolder below.</span>
            </div>
          )}
          {!downloadConfirm.loading && downloadConfirm.subfolders.filter((s) => s.count > 0).length > 0 && (
            <div className="space-y-2" data-testid="download-subfolders">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#5C5C5C]">Or download just a subfolder</p>
              <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
                {downloadConfirm.subfolders.filter((s) => s.count > 0).map((sub) => (
                  <button
                    key={sub.id}
                    onClick={() => downloadSubfolder(sub)}
                    className="w-full flex items-center justify-between gap-3 rounded-lg border border-slate-200 hover:border-[#34657f] hover:bg-[#34657f]/5 px-3 py-2 text-left transition-colors"
                    data-testid={`download-subfolder-${sub.id}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Folder className="w-4 h-4 text-[#34657f] shrink-0" />
                      <span className="text-sm text-[#1A1A1A] truncate">{sub.name}</span>
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-xs text-[#5C5C5C]">{sub.count} file{sub.count === 1 ? '' : 's'}</span>
                      <Download className="w-4 h-4 text-[#5C5C5C]" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDownloadConfirm((prev) => ({ ...prev, open: false }))} data-testid="download-confirm-cancel">
              Cancel
            </Button>
            <Button
              className="bg-[#b34e00] hover:bg-[#a84d09]"
              disabled={downloadConfirm.loading}
              onClick={confirmFolderDownload}
              data-testid="download-confirm-proceed"
            >
              <Download className="w-4 h-4 mr-2" />
              Download{downloadConfirm.count !== null ? ` ${downloadConfirm.count} file${downloadConfirm.count === 1 ? '' : 's'}` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={renameFolderOpen} onOpenChange={(open) => {
        setRenameFolderOpen(open);
        if (!open) { setRenameFolderTarget(null); setRenameFolderValue(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Rename Folder</DialogTitle>
            <DialogDescription>Enter a new name for this folder.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="rename-folder-input">Folder Name</Label>
              <Input
                id="rename-folder-input"
                value={renameFolderValue}
                onChange={(e) => setRenameFolderValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFolder(); }}
                data-testid="rename-folder-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFolderOpen(false)}>Cancel</Button>
            <Button onClick={handleRenameFolder} className="bg-[#34657f]" data-testid="confirm-rename-folder-btn">
              Rename
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder Appearance Dialog */}
      <Dialog open={appearanceOpen} onOpenChange={(open) => {
        setAppearanceOpen(open);
        if (!open) { setAppearanceTarget(null); }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="folder-appearance-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Customize Folder</DialogTitle>
            <DialogDescription>Choose a color and icon for "{appearanceTarget?.name}".</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            {/* Live preview */}
            <div className="flex items-center gap-3">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                style={{ backgroundColor: appearanceColor || '#34657f' }}
              >
                {(() => { const Ic = FOLDER_ICONS[appearanceIcon] || Folder; return <Ic className="w-7 h-7 text-white" />; })()}
              </div>
              <span className="text-sm text-[#5C5C5C]">Preview</span>
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {FOLDER_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setAppearanceColor(c)}
                    className={`w-8 h-8 rounded-full transition-transform hover:scale-110 ${appearanceColor === c ? 'ring-2 ring-offset-2 ring-[#1A1A1A]' : ''}`}
                    style={{ backgroundColor: c }}
                    aria-label={`Color ${c}`}
                    data-testid={`appearance-color-${c}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="color"
                  value={appearanceColor || '#34657f'}
                  onChange={(e) => setAppearanceColor(e.target.value)}
                  className="w-9 h-9 rounded-md border border-slate-200 cursor-pointer bg-white p-0.5"
                  aria-label="Pick a custom color"
                  data-testid="appearance-color-custom"
                />
                <Input
                  value={appearanceColor || ''}
                  onChange={(e) => {
                    let v = e.target.value.trim();
                    if (v && !v.startsWith('#')) v = '#' + v;
                    setAppearanceColor(v);
                  }}
                  placeholder="#34657f"
                  className="h-9 w-28 font-mono text-sm"
                  data-testid="appearance-color-hex"
                />
                <span className="text-xs text-[#5C5C5C]">Pick any color</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-8 gap-2">
                {Object.entries(FOLDER_ICONS).map(([name, Ic]) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setAppearanceIcon(name)}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-colors ${appearanceIcon === name ? 'border-[#34657f] bg-[#34657f]/10 text-[#34657f]' : 'border-slate-200 text-[#5C5C5C] hover:bg-slate-50'}`}
                    aria-label={`Icon ${name}`}
                    data-testid={`appearance-icon-${name}`}
                  >
                    <Ic className="w-5 h-5" />
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={handleResetAppearance} className="mr-auto text-[#5C5C5C]" data-testid="appearance-reset-btn">
              Reset to default
            </Button>
            <Button variant="outline" onClick={() => setAppearanceOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAppearance} className="bg-[#34657f] hover:bg-[#2a5060]" data-testid="appearance-save-btn">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">
              Delete {deleteType === 'folder' ? 'Folder' : 'Document'}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.name || deleteTarget?.title}"? 
              This action cannot be undone.
              {deleteType === 'folder' && (
                <span className="block mt-2 font-medium">
                  Note: You can only delete empty folders.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Document Preview Dialog */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-5xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2 pr-8">
              <Eye className="w-5 h-5 text-[#34657f] shrink-0" />
              <span className="truncate min-w-0" title={previewDoc?.title}>{previewDoc?.title}</span>
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative flex-1 min-h-[400px] bg-slate-100 rounded-lg overflow-hidden">
            {previewDoc && getPreviewUrl(previewDoc) ? (
              <iframe
                src={getPreviewUrl(previewDoc)}
                className="w-full h-full"
                title={previewDoc.title}
                allow="autoplay"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#5C5C5C]">
                No preview available for this file.
              </div>
            )}
            {isDemo && previewDoc && getPreviewUrl(previewDoc) && (
              <div
                aria-hidden="true"
                data-testid="demo-preview-watermark"
                className="pointer-events-none absolute inset-0 z-10"
                style={demoWatermarkStyle}
              />
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreviewDoc(null)}>
              Close
            </Button>
            {!isDemo && previewDoc?.drive_file_id && (
              <Button
                variant="outline"
                onClick={() => makeCopyInDrive(previewDoc)}
                className="text-[#b45309] font-medium border-[#e87722]/50 hover:bg-[#e87722]/10"
                data-testid="preview-copy-drive"
              >
                <Copy className="w-4 h-4 mr-2" />
                Make a copy in my Drive
              </Button>
            )}
            {!isDemo && (
            <Button 
              onClick={() => triggerDownload(previewDoc, getDriveNativeKind(previewDoc) ? 'pdf' : 'original')}
              className="bg-[#34657f] hover:bg-[#2a5060]"
            >
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy destination chooser */}
      <Dialog open={!!copyDestPrompt} onOpenChange={(o) => !o && setCopyDestPrompt(null)}>
        <DialogContent className="max-w-md" data-testid="copy-dest-dialog">
          <DialogHeader>
            <DialogTitle>Where should the copy go?</DialogTitle>
            <DialogDescription>
              Copying {copyDestPrompt?.kind === 'folder' ? 'the folder ' : ''}
              <span className="font-semibold text-[#1A1A1A]">"{copyDestPrompt?.target?.name || copyDestPrompt?.target?.title}"</span> to your Google Drive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Button
              className="w-full justify-start gap-2 bg-[#34657f] hover:bg-[#2a5060]"
              onClick={() => startCopyTo('mydrive')}
              data-testid="copy-dest-mydrive-btn"
            >
              <Cloud className="w-4 h-4" />
              My Drive (top level)
            </Button>
            {lastDest && (
              <Button
                variant="outline"
                className="w-full justify-start gap-2 border-[#6cc24a]/50 text-[#3d7a22] hover:bg-[#6cc24a]/10"
                onClick={() => startCopyTo('last')}
                data-testid="copy-dest-last-btn"
              >
                <FolderOpen className="w-4 h-4" />
                <span className="truncate">{lastDest.name || 'Last used folder'} <span className="text-xs opacity-70">(last used)</span></span>
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => startCopyTo('pick')}
              data-testid="copy-dest-pick-btn"
            >
              <FolderOpen className="w-4 h-4" />
              Choose a folder in my Drive…
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Google Forms manual-copy prompt (after folder copy) */}
      <Dialog open={!!formCopyPrompt} onOpenChange={(o) => !o && closeFormPrompt()}>
        <DialogContent className="max-w-md" data-testid="form-copy-dialog">
          {formCopyPrompt?.phase === 'move' ? (
            <>
              <DialogHeader>
                <DialogTitle>File your form copies into the folder</DialogTitle>
                <DialogDescription>
                  Google saves form copies at the top of your Drive. Once you've clicked <span className="font-semibold">Make a copy</span> on
                  each Google tab, click below and select your new copies (they'll be first in the list) — we'll move them into
                  {' '}"{formCopyPrompt?.folderName}" for you.
                  {driveEmailRef.current && (
                    <span className="block mt-2 text-xs text-[#64707a]">
                      Connected Google account: <span className="font-semibold">{driveEmailRef.current}</span>. Only fresh copies of the portal's forms will be moved — anything else you select is skipped.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => closeFormPrompt()} data-testid="form-move-skip-btn">Skip</Button>
                <Button className="bg-[#34657f] hover:bg-[#2a5060]" onClick={moveFormCopies} data-testid="form-move-pick-btn">
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Select my form copies
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Copy Google Forms to your Drive</DialogTitle>
                <DialogDescription>
                  {formCopyPrompt?.links.length === 1 ? 'One Google Form' : `${formCopyPrompt?.links.length} Google Forms`} in
                  {' '}"{formCopyPrompt?.folderName}" need{formCopyPrompt?.links.length === 1 ? 's' : ''} one extra click.
                  Click each form below, then choose <span className="font-semibold">Make a copy</span> on the Google page — the copy is saved to your Drive.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 max-h-72 overflow-y-auto">
            {formCopyPrompt?.links.map((f, i) => (
              <Button
                key={f.copy_url}
                variant="outline"
                className="w-full justify-start gap-2 h-auto py-2 whitespace-normal text-left text-[#b45309] font-medium border-[#e87722]/40 hover:bg-[#e87722]/10"
                onClick={() => {
                  window.open(f.copy_url, '_blank', 'noopener');
                  setFormCopyPrompt((prev) => {
                    if (!prev) return prev;
                    const rest = prev.links.filter((_, idx) => idx !== i);
                    if (!rest.length) {
                      if (prev.destDriveFolderId) return { ...prev, links: [], phase: 'move' };
                      toast.success('All forms copied — check your Google Drive!');
                      setTimeout(() => closeFormPrompt(), 0);
                      return null;
                    }
                    return { ...prev, links: rest };
                  });
                }}
                data-testid={`form-copy-link-${i}`}
              >
                <Copy className="w-4 h-4 shrink-0" />
                {f.title}
              </Button>
            ))}
          </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => closeFormPrompt()} data-testid="form-copy-done-btn">Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Copy receipt — exactly what landed in the user's Drive */}
      <Dialog open={!!copyReceipt} onOpenChange={(o) => !o && setCopyReceipt(null)}>
        <DialogContent className="max-w-lg" data-testid="copy-receipt-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#3d7a22]" aria-hidden="true" />
              Copy receipt — "{copyReceipt?.title}"
            </DialogTitle>
            <DialogDescription>
              {copyReceipt?.files?.length || 0} file{(copyReceipt?.files?.length || 0) === 1 ? '' : 's'} copied to your Google Drive
              {driveEmailRef.current ? ` (${driveEmailRef.current})` : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1 pr-1" data-testid="copy-receipt-list">
            {(copyReceipt?.files || []).map((f, i) => (
              <div key={i} className="flex items-start justify-between gap-2 text-sm py-1.5 px-2 rounded hover:bg-slate-50" data-testid={`receipt-file-${i}`}>
                <span className="flex items-start gap-2 text-[#1A1A1A] min-w-0">
                  <CheckCircle2 className="w-4 h-4 text-[#3d7a22] shrink-0 mt-0.5" aria-hidden="true" />
                  <span className="break-words">{f.title}</span>
                </span>
                {f.link && (
                  <a href={f.link} target="_blank" rel="noopener noreferrer" className="text-[#1a6f92] underline text-xs font-medium shrink-0 py-1" aria-label={`Open ${f.title} in Google Drive`}>
                    Open
                  </a>
                )}
              </div>
            ))}
            {(copyReceipt?.skipped || []).map((s, i) => (
              <div key={`s-${i}`} className="flex items-start gap-2 text-sm py-1.5 px-2 rounded bg-amber-50" data-testid={`receipt-skipped-${i}`}>
                <AlertTriangle className="w-4 h-4 text-[#b45309] shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-[#78350f] break-words">{s.title} — <span className="italic">{s.reason}</span></span>
              </div>
            ))}
            {(copyReceipt?.formsPending || []).length > 0 && (
              <div className="text-xs text-[#64707a] pt-2 border-t border-slate-100 mt-2">
                Not included above: {copyReceipt.formsPending.length} Google Form{copyReceipt.formsPending.length === 1 ? '' : 's'} ({copyReceipt.formsPending.join(', ')}) — forms are copied via Google's "Make a copy" step.
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {copyReceipt?.folderLink && (
              <Button
                variant="outline"
                onClick={() => window.open(copyReceipt.folderLink, '_blank', 'noopener')}
                data-testid="receipt-open-folder-btn"
              >
                <FolderOpen className="w-4 h-4 mr-2" />
                Open folder in Drive
              </Button>
            )}
            <Button className="bg-[#34657f] hover:bg-[#2a5060]" onClick={() => setCopyReceipt(null)} data-testid="receipt-close-btn">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CurriculumPage;
